import React from 'react';
import { createPortal } from 'react-dom';
import api, { errMsg } from '../api';
import { useSpc } from '../context/SpcContext';
import { AchievementCabinet, AchIcon, catColor } from './Achievements';
import { AvatarUploader } from './views/Profile';
import { formatDisplay } from '../lib/dates';
import { KIT_CATEGORIES, kitCategoryLabel, formatListingPrice, isNumericPrice, numericPriceValue } from '../lib/kit';
import { fileToResizedDataURL } from '../lib/images';
import { MultiImagePicker } from './ImagePicker';
import { googleCalendarUrl, downloadIcs } from '../lib/calendar';
import { Lightbox } from './ImageGallery';
import AvatarCrop from './AvatarCrop';
import { FORMATS } from './views/Resources';
import { SCInfoContent } from './ScInfoBadge';
import { Camera, BellRing, Bell, X, Send, UserPlus, Pencil, Trash2, UserCheck, Trophy, Copy, CheckCircle2, MessageCircle, ShieldCheck, Share, SquarePlus, Upload, Clock, MapPin, PartyPopper, Tag, Flame, SmilePlus, ChevronDown, Megaphone, Calendar, Download, Sparkles, ArrowRightLeft, Info } from 'lucide-react';

function Field({ label, children }) { return <label>{label}{children}</label>; }

// Role names that unlock an achievement badge (mirrors backend _achievement_progress_for in server.py)
// — used to show the matching badge icon next to a role pill in Manage Members.
const ROLE_BADGE_ACHIEVEMENTS = { Coach: 'coach_cert', 'Run Leader': 'coach_cert', 'Coached Athlete': 'coached_start' };
function RoleBadgePill({ roleName, achievementCatalogue, memberId }) {
  const achId = ROLE_BADGE_ACHIEVEMENTS[roleName];
  const ach = achId ? (achievementCatalogue || []).find(a => a.id === achId) : null;
  return (
    <span
      className={`pill ${ach ? 'cat-tinted' : 'yellow'}`}
      style={{ padding: '2px 8px', fontSize: 11, ...(ach ? { '--cat-color': catColor(ach.category) } : {}) }}
      title={ach ? `Unlocks the "${ach.title}" achievement badge` : undefined}
      data-testid={`member-rolename-${memberId}`}
    >
      {ach && <AchIcon a={ach} size={12} />}{roleName}
    </span>
  );
}

function MessagesInbox({ ctx, state }) {
  const [tab, setTab] = React.useState('chats');
  const convs = state.conversations || [];
  const chats = convs.filter(c => !c.listingId);
  const marketplace = convs.filter(c => c.listingId);
  const list = tab === 'chats' ? chats : marketplace;
  const row = (c) => {
    const last = c.messages[c.messages.length - 1];
    return (
      <div className={`item ${c.unread ? 'unread-item' : ''}`} data-testid={`conversation-${c.id}`} key={c.id} style={{ cursor: 'pointer' }} onClick={() => ctx.openModal('thread', { id: c.id })}>
        <div className="item-main">
          <div className="item-title">{c.withName} {c.unread ? <span className="dot-badge">{c.unread}</span> : null}</div>
          {c.listingTitle && <div className="meta"><span>{c.listingTitle}</span></div>}
          <div style={{ color: '#c4c8cd', fontSize: 13, marginTop: 6 }}>{last ? (last.from === 'me' ? 'You: ' : '') + (last.text || (last.image ? '📷 Photo' : '')) : ''}</div>
        </div>
        <BellRing size={16} />
      </div>
    );
  };
  return (
    <div>
      <div className="arcade-tabs" role="tablist" data-testid="messages-tabs">
        <button role="tab" type="button" className={`arcade-tab ${tab === 'chats' ? 'active' : ''}`} data-testid="messages-tab-chats" onClick={() => setTab('chats')}>
          Chats{chats.some(c => c.unread) ? <span className="dot-badge" style={{ marginLeft: 6 }} /> : null}
        </button>
        <button role="tab" type="button" className={`arcade-tab ${tab === 'marketplace' ? 'active' : ''}`} data-testid="messages-tab-marketplace" onClick={() => setTab('marketplace')}>
          Marketplace{marketplace.some(c => c.unread) ? <span className="dot-badge" style={{ marginLeft: 6 }} /> : null}
        </button>
      </div>
      {tab === 'chats' && (
        <button className="btn secondary" data-testid="new-message-btn" style={{ width: '100%', margin: '14px 0' }} onClick={() => ctx.openModal('newMessage')}>
          <UserPlus size={15} /> New message
        </button>
      )}
      {list.length ? (
        <div className="list" style={{ marginTop: tab === 'marketplace' ? 14 : 0 }} data-testid={`messages-list-${tab}`}>{list.map(row)}</div>
      ) : (
        <div className="empty" data-testid={`messages-empty-${tab}`} style={{ marginTop: tab === 'marketplace' ? 14 : 0 }}>
          {tab === 'chats' ? 'No chats yet — tap “New message” above.' : 'No marketplace chats yet — tap “I’m interested” on any listing to start one.'}
        </div>
      )}
    </div>
  );
}

function tenureLabel(iso) {
  if (!iso) return null;
  const start = new Date(iso);
  if (isNaN(start.getTime())) return null;
  const now = new Date();
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  if (months < 1) return 'Joined this month';
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} with SPC`;
  const years = Math.floor(months / 12), rem = months % 12;
  return `${years} year${years === 1 ? '' : 's'}${rem ? ` ${rem} mo` : ''} with SPC`;
}

function lastLoginLabel(iso) {
  if (!iso) return 'Never logged in';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Never logged in';
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'Last seen just now';
  if (mins < 60) return `Last seen ${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Last seen ${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `Last seen ${days}d ago`;
  return `Last seen ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

function MyAccount({ state, user, updateAvatar, updateLeaderboard, enablePush, disablePush, sendTestPush, changeEmail, changePassword, toast }) {
  const [email, setEmail] = React.useState('');
  const [emailPw, setEmailPw] = React.useState('');
  const [curPw, setCurPw] = React.useState('');
  const [newPw, setNewPw] = React.useState('');
  const [confirmPw, setConfirmPw] = React.useState('');
  const [savingEmail, setSavingEmail] = React.useState(false);
  const [savingPw, setSavingPw] = React.useState(false);

  const [lbName, setLbName] = React.useState(user?.leaderboardName || '');
  const [lbAnon, setLbAnon] = React.useState(!!user?.leaderboardAnonymous);
  const [lbOptOut, setLbOptOut] = React.useState(!!user?.leaderboardOptOut);
  const lbPlaceholder = (() => { const p = (user?.name || '').split(' ').filter(Boolean); return p.length >= 2 ? `${p[0]} ${p[1][0]}.` : (p[0] || 'Your name'); })();

  const hasNotifAPI = typeof Notification !== 'undefined';
  const denied = hasNotifAPI && Notification.permission === 'denied';
  const optedOut = localStorage.getItem('spc_push_opt_out') === '1';
  const [pushChecked, setPushChecked] = React.useState(hasNotifAPI ? (Notification.permission !== 'denied' && !optedOut) : false);
  const [pushBusy, setPushBusy] = React.useState(false);
  const [sendingTest, setSendingTest] = React.useState(false);

  // The toggle above is an optimistic guess from permission state alone — verify a real,
  // working subscription actually exists (it can silently fail/go stale, esp. on iOS).
  const [pushDiag, setPushDiag] = React.useState(null);
  const checkPushDiag = React.useCallback(async () => {
    if (!hasNotifAPI || Notification.permission === 'denied' || optedOut || !('serviceWorker' in navigator)) {
      setPushChecked(false);
      setPushDiag({ permission: hasNotifAPI ? Notification.permission : 'unsupported', subscribed: false, backendSaved: false, endpoint: null });
      return;
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (!sub) {
        setPushChecked(false);
        setPushDiag({ permission: Notification.permission, subscribed: false, backendSaved: false, endpoint: null });
        return;
      }
      // Browser having a subscription doesn't mean the backend actually has it saved — that
      // POST can fail silently (network hiccup, etc). Re-confirm/repair it here, for real.
      let backendSaved = false;
      let saveError = null;
      try {
        await api.post('/push/subscribe', { subscription: sub.toJSON() });
        backendSaved = true;
      } catch (e) {
        console.error('[SPC push] backend re-sync save failed:', e);
        saveError = e?.response?.data?.detail || e?.message || 'save failed';
      }
      setPushChecked(backendSaved);
      setPushDiag({ permission: Notification.permission, subscribed: true, backendSaved, endpoint: new URL(sub.endpoint).hostname, error: saveError });
    } catch (e) {
      console.error('[SPC push] diagnostic check failed:', e);
      setPushChecked(false);
      setPushDiag({ permission: Notification.permission, subscribed: false, backendSaved: false, endpoint: null, error: String(e?.message || e) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => { checkPushDiag(); }, [checkPushDiag]);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email || !emailPw) return;
    setSavingEmail(true);
    const ok = await changeEmail(email, emailPw);
    setSavingEmail(false);
    if (ok) { setEmail(''); setEmailPw(''); }
  };
  const submitPassword = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { toast("New passwords don't match"); return; }
    setSavingPw(true);
    const ok = await changePassword(curPw, newPw);
    setSavingPw(false);
    if (ok) { setCurPw(''); setNewPw(''); setConfirmPw(''); }
  };
  const onPushChange = async (e) => {
    const next = e.target.checked;
    setPushBusy(true);
    const ok = next ? await enablePush() : await disablePush();
    setPushChecked(next ? !!ok : false);
    await checkPushDiag();
    setPushBusy(false);
  };
  const onSendTestPush = async () => {
    setSendingTest(true);
    await sendTestPush();
    await checkPushDiag();
    setSendingTest(false);
  };

  return (
    <div data-testid="my-account-panel" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div className="member-id">
        <AvatarUploader user={user} updateAvatar={updateAvatar} toast={toast} />
        <div><h3 style={{ marginTop: 0 }}>{user?.name}</h3><p>{user?.tier || 'Member'} · Member since {state.member?.memberSince || '2025'}</p></div>
      </div>
      {user?.role !== 'Admin' && (
        <div className="meta">
          <span>{state.member?.sessionsAttended || 0} sessions attended</span>
          <span>{(state.unlockedAchievements || []).length} {(state.unlockedAchievements || []).length === 1 ? 'achievement' : 'achievements'}</span>
        </div>
      )}

      <form className="form" onSubmit={submitEmail} data-testid="change-email-form">
        <h3 style={{ margin: 0 }}>Change email</h3>
        <Field label="New email address"><input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={user?.email} data-testid="change-email-input" /></Field>
        <Field label="Current password (to confirm)"><input type="password" value={emailPw} onChange={e => setEmailPw(e.target.value)} data-testid="change-email-password-input" /></Field>
        <button className="btn" disabled={savingEmail} data-testid="change-email-submit">{savingEmail ? 'Saving…' : 'Update email'}</button>
      </form>

      <form className="form" onSubmit={submitPassword} data-testid="change-password-form">
        <h3 style={{ margin: 0 }}>Change password</h3>
        <Field label="Current password"><input type="password" value={curPw} onChange={e => setCurPw(e.target.value)} data-testid="change-password-current-input" /></Field>
        <Field label="New password"><input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} data-testid="change-password-new-input" /></Field>
        <Field label="Confirm new password"><input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} data-testid="change-password-confirm-input" /></Field>
        <button className="btn" disabled={savingPw} data-testid="change-password-submit">{savingPw ? 'Saving…' : 'Update password'}</button>
      </form>

      <div className="card" data-testid="leaderboard-settings">
        <span className="pill yellow"><Trophy size={13} style={{ marginRight: 5, verticalAlign: '-2px' }} />LEADERBOARD SETTINGS</span>
        <h3 style={{ marginTop: 10 }}>How you show up on the SPC Leaderboard</h3>
        <p>Choose the name other members see, go anonymous, or hide yourself completely.</p>
        <div className="lb-settings">
          <div className="lb-set-field">
            <label>Display name</label>
            <input value={lbName} data-testid="lb-name-input" onChange={(e) => setLbName(e.target.value)} placeholder={lbPlaceholder} disabled={lbAnon} maxLength={24} />
            <small>Leave blank to use “{lbPlaceholder}”.</small>
          </div>
          <label className="repeat-row" data-testid="lb-anon-toggle">
            <input type="checkbox" checked={lbAnon} onChange={(e) => setLbAnon(e.target.checked)} />
            <span><strong>Appear anonymously</strong><small>Show as “Anonymous Runner” with no name or photo.</small></span>
          </label>
          <label className="repeat-row" data-testid="lb-optout-toggle">
            <input type="checkbox" checked={lbOptOut} onChange={(e) => setLbOptOut(e.target.checked)} />
            <span><strong>Hide me from the leaderboard</strong><small>You won't appear anywhere on the rankings.</small></span>
          </label>
        </div>
        <button className="btn" data-testid="lb-save-btn" style={{ marginTop: 14 }} onClick={() => updateLeaderboard({ name: lbName, anonymous: lbAnon, optOut: lbOptOut })}>Save leaderboard settings</button>
      </div>

      <div className="card" data-testid="push-settings">
        <span className="pill yellow"><Bell size={13} style={{ marginRight: 5, verticalAlign: '-2px' }} />PUSH NOTIFICATIONS</span>
        <h3 style={{ marginTop: 10 }}>Stay in the loop</h3>
        <p>Get pinged for new messages, Marketplace activity, bookings and club announcements. On by default — turn off any time.</p>
        {hasNotifAPI ? (
          <label className="repeat-row" data-testid="push-toggle">
            <input type="checkbox" checked={pushChecked} disabled={pushBusy || denied} onChange={onPushChange} />
            <span><strong>Push notifications</strong><small>{denied ? 'Blocked — enable in your phone/browser Settings → Notifications' : 'Get instant alerts on this device.'}</small></span>
          </label>
        ) : (
          <div className="notice">On iPhone, add SPC to your Home Screen first (Share → Add to Home Screen), then push notifications will be available here.</div>
        )}
        {hasNotifAPI && pushChecked && (
          <button type="button" className="btn secondary" style={{ marginTop: 12 }} disabled={sendingTest} onClick={onSendTestPush} data-testid="send-test-push-btn">
            {sendingTest ? 'Sending…' : 'Send me a test push'}
          </button>
        )}
        {pushDiag && (
          <div className="push-diag" data-testid="push-diagnostic" style={{ marginTop: 12, fontSize: 12, color: '#8b8e93', fontFamily: 'monospace', lineHeight: 1.7 }}>
            <div>permission: <strong style={{ color: pushDiag.permission === 'granted' ? '#7cd992' : '#e1e20a' }}>{pushDiag.permission}</strong></div>
            <div>real subscription on this device: <strong style={{ color: pushDiag.subscribed ? '#7cd992' : '#ff6b6b' }}>{pushDiag.subscribed ? 'YES' : 'NO'}</strong></div>
            {pushDiag.endpoint && <div>push service: {pushDiag.endpoint}</div>}
            {pushDiag.error && <div style={{ color: '#ff6b6b' }}>error: {pushDiag.error}</div>}
          </div>
        )}
      </div>
    </div>
  );
}


export default function Modals() {
  const ctx = useSpc();
  const { modal, closeModal } = ctx;
  const downOnBackdrop = React.useRef(false);
  React.useEffect(() => {
    if (!modal) return;
    const onKey = (e) => { if (e.key === 'Escape') closeModal(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modal, closeModal]);
  if (!modal) return null;
  const { title, body } = renderModal(ctx);
  // Only close when the user BOTH pressed down AND released on the backdrop itself.
  // This prevents accidental closes when dragging to select text inside an input
  // and releasing the mouse over the backdrop.
  const onBackdropMouseDown = (e) => { downOnBackdrop.current = e.target === e.currentTarget; };
  const onBackdropMouseUp = (e) => {
    if (downOnBackdrop.current && e.target === e.currentTarget) closeModal();
    downOnBackdrop.current = false;
  };
  return (
    <div
      className="modal-backdrop open"
      onMouseDown={onBackdropMouseDown}
      onMouseUp={onBackdropMouseUp}
    >
      <div className="modal" data-testid="modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head"><h3 data-testid="modal-title">{title}</h3><button className="close" data-testid="modal-close" onClick={closeModal}>×</button></div>
        <div>{body}</div>
      </div>
    </div>
  );
}

function NewMessagePicker({ ctx }) {
  const [members, setMembers] = React.useState(null);
  const [q, setQ] = React.useState('');
  React.useEffect(() => {
    ctx.getMembersDirectory().then(({ data }) => setMembers(data.members || [])).catch(() => setMembers([]));
  }, [ctx]);
  const filtered = (members || []).filter(m => m.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div data-testid="new-message-picker">
      <input
        className="new-message-search"
        placeholder="Search members…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        data-testid="new-message-search-input"
        autoFocus
      />
      {members === null ? (
        <div className="empty">Loading members…</div>
      ) : filtered.length === 0 ? (
        <div className="empty">No members found.</div>
      ) : (
        <div className="list" style={{ marginTop: 10 }}>
          {filtered.map(m => (
            <div className="item" key={m.id} style={{ cursor: 'pointer' }} data-testid={`directory-member-${m.id}`}
              onClick={() => { ctx.startConversation(m.id); }}>
              <div className="item-main">
                <div className="item-title">{m.name}</div>
                {m.tier && <div className="meta">{m.tier}</div>}
              </div>
              <MessageCircle size={16} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const NOTIF_CATEGORIES = [
  { id: 'messages', label: 'Messages', desc: 'New chats, and reactions to your messages' },
  { id: 'challenges', label: 'SPC Challenges', desc: 'New challenges, winners and submission reviews' },
  { id: 'sessions', label: 'Sessions & Bookings', desc: 'Reminders, changes and cancellations' },
  { id: 'marketplace', label: 'Marketplace', desc: 'New listings, offers and interest on your items' },
  { id: 'points', label: 'Points & Achievements', desc: 'Referrals joining and achievement unlocks' },
  { id: 'polls', label: 'Weekly Polls', desc: 'When a new session poll opens' },
  { id: 'attendance', label: 'Check-ins', desc: 'Confirmation when you\u2019re checked in at a session' },
  { id: 'membership', label: 'Membership', desc: 'Renewal reminders' },
  { id: 'announcements', label: 'Club Announcements', desc: 'News and updates from SPC' },
];

function NotificationPrefsPanel({ ctx, state }) {
  const saved = state.member?.notificationPrefs || {};
  const [prefs, setPrefs] = React.useState(() => {
    const p = {};
    NOTIF_CATEGORIES.forEach(c => { p[c.id] = saved[c.id] !== false; });
    return p;
  });
  const majorityOn = NOTIF_CATEGORIES.filter(c => prefs[c.id]).length >= NOTIF_CATEGORIES.length / 2;
  const toggle = (id) => {
    const next = { ...prefs, [id]: !prefs[id] };
    setPrefs(next);
    ctx.updateNotificationPrefs({ [id]: next[id] });
  };
  const setAll = (on) => {
    const next = {};
    NOTIF_CATEGORIES.forEach(c => { next[c.id] = on; });
    setPrefs(next);
    ctx.updateNotificationPrefs(next);
  };
  return (
    <div data-testid="notification-prefs-panel">
      <div className="notice">Choose what you get pushed to your phone. Every notification still appears in the bell either way — this only controls push alerts.</div>
      <button className="btn secondary" style={{ width: '100%', margin: '12px 0' }} data-testid="notif-prefs-toggle-all" onClick={() => setAll(!majorityOn)}>
        {majorityOn ? 'Turn all off' : 'Turn all on'}
      </button>
      <div className="list">
        {NOTIF_CATEGORIES.map(c => (
          <label className="item notif-pref-row" key={c.id} data-testid={`notif-pref-${c.id}`}>
            <div className="item-main"><div className="item-title">{c.label}</div><div className="meta">{c.desc}</div></div>
            <input type="checkbox" checked={!!prefs[c.id]} onChange={() => toggle(c.id)} data-testid={`notif-pref-${c.id}-checkbox`} />
          </label>
        ))}
      </div>
    </div>
  );
}

const HELP_SECTIONS = [
  {
    title: 'Your Membership Card & PIN',
    body: "Head to My SPC to see your Membership Card — name, tier, and a 5-digit Member PIN. Give this PIN to a coach at any session and they'll check you in, which earns SPC Points. You can download the card as an image to save or print any time.",
  },
  {
    title: 'Booking sessions',
    body: "Tap Book to see what's on. Bookings are free unless a session shows a price — paid sessions go through a quick card checkout before your spot is confirmed. If a session is full, join the waitlist and you'll be notified the moment a spot opens. Made a booking you can't make? Just tap Cancel — it only affects that one session.",
  },
  {
    title: 'Weekly Classes & block booking',
    body: "Some Classes run as a weekly block (e.g. a 6-week course). If a class shows a single 'Book all N weeks · one payment' button, that's a block booking — you pay once and are automatically booked into every week. If you can't make a specific week, tap \"Can't make it this week\" — you'll stay booked on the rest of the block. Classes without the block option can be booked one week at a time as normal.",
  },
  {
    title: 'Challenges',
    body: "Head to More → Challenges to see the club's current challenge. Log your effort with a photo or note (up to 4 photos) — a coach reviews and approves each entry, and approved entries earn SPC Points plus can unlock achievement badges.",
  },
  {
    title: 'SPC Points & prizes',
    body: "You earn SPC Points for turning up to sessions, completing challenges, taking part in club activity and referring new members. Check your balance, how points are earned, and your full points history on the Points page — then redeem points for real prizes in the same section.",
  },
  {
    title: 'Points & achievements',
    body: "Tap Points to see the club leaderboard and your unlocked achievement badges. Sessions, challenges, referrals and races all add points and can unlock badges — some legendary ones are hidden, so keep grinding.",
  },
  {
    title: 'Marketplace (Sell/Swap Shop)',
    body: "List old kit for free on the Sell page — add photos, a price, condition and category. You can edit your own listing any time (title, description, price, photos) and tap any photo to view it full-screen.",
  },
  {
    title: 'Messages',
    body: "Tap the message icon in the header to chat directly with other members or coaches — handy for organising a run buddy or asking a quick question.",
  },
  {
    title: 'Race Hub',
    body: "Use Race Hub (in the More menu) to log your race times so they count toward club records and your profile.",
  },
  {
    title: 'Referrals',
    body: "Your unique referral code is on your Membership Card and the Points page. Share it with a friend — they enter it when signing up, and once they become an active paying member you earn referral points and unlock referral badges.",
  },
  {
    title: 'Notifications & the WhatsApp group',
    body: "Turn on push notifications when prompted for session reminders, messages and waitlist promotions. For club banter and last-minute changes, join the SPC WhatsApp group from the More menu.",
  },
];

const ADMIN_HELP_SECTIONS = [
  {
    title: 'Managing members',
    body: "More → Manage members lets you approve new sign-ups, edit member details, and assign one or more custom roles (Coach, Run Leader, Committee, etc.) via the Edit form — a member can hold several roles at once.",
  },
  {
    title: 'Creating & editing sessions',
    body: "Use + Create on the Book page to add a session. Tick 'Repeat weekly' to create a recurring series in one go, and for Classes you can also tick 'This is a block booking' so members pay once for the whole block instead of week-by-week. Edit any session (or the whole series) from its Manage button.",
  },
  {
    title: 'Reviewing challenge submissions',
    body: "More → Challenges → Review lets you approve or flag member submissions, awarding points on approval.",
  },
  {
    title: 'Points, rewards & roles',
    body: "My SPC → Admin Centre has tools for the SPC Points ledger (manual awards/corrections), reward redemptions, custom roles & permissions, and discount codes for sign-up.",
  },
];

function HelpAccordionItem({ title, body, testId }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="card points-earned-card" style={{ marginBottom: 10 }}>
      <button type="button" className="points-earned-toggle" onClick={() => setOpen(v => !v)} data-testid={testId}>
        <h3 style={{ fontSize: 15 }}>{title}</h3>
        <ChevronDown size={16} className={`points-earned-chevron ${open ? 'open' : ''}`} />
      </button>
      {open && <p style={{ marginTop: 10 }}>{body}</p>}
    </div>
  );
}

function HelpCenter({ role, state }) {
  const whatsappUrl = state?.settings?.whatsappUrl;
  return (
    <div data-testid="help-center">
      <div className="notice" style={{ marginBottom: 14 }}>
        Already done the Welcome tour? This is the full reference — everything you need to know about using the Clubhouse, all in one place.
      </div>
      {HELP_SECTIONS.map((s, i) => (
        <HelpAccordionItem key={s.title} title={s.title} body={s.body} testId={`help-item-${i}`} />
      ))}
      {role === 'admin' && (
        <>
          <div className="notice soft" style={{ margin: '18px 0 10px' }}><strong>Admin tools</strong></div>
          {ADMIN_HELP_SECTIONS.map((s, i) => (
            <HelpAccordionItem key={s.title} title={s.title} body={s.body} testId={`help-admin-item-${i}`} />
          ))}
        </>
      )}
      <div className="notice" style={{ marginTop: 14 }}>
        Still stuck? {whatsappUrl ? <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">Ask in the SPC WhatsApp group</a> : 'Ask a coach at any session'} or email info@smartperformancecoaching.co.uk.
      </div>
    </div>
  );
}


function renderModal(ctx) {
  const { state, modal, toast, openSPC, role } = ctx;
  const { type, payload } = modal;
  switch (type) {
    case 'help':
      return { title: 'Help & how to use Clubhouse', body: <HelpCenter role={role} state={state} /> };
    case 'scInfo':
      return { title: 'S&C Block Info', body: <SCInfoContent /> };
    case 'paceInfo':
      return {
        title: 'Pace groups explained',
        body: (
          <div className="pace-info" data-testid="pace-info-modal">
            <p>
              Pace groups keep runners of a similar speed together, so everyone has good company and a sensible
              target pace for the session.
            </p>
            <ul>
              <li><strong>🔵 Blue</strong> — our steadiest, social pace. Great if you're building up or prefer a relaxed run.</li>
              <li><strong>🟡 Yellow</strong> — a quicker, tempo-ish pace for those wanting more of a push.</li>
            </ul>
            <p>
              Each group's range is shown per mile and per km — pick whichever is closest to your natural running
              speed. You're always welcome to run a little faster or slower than your group on the day.
            </p>
          </div>
        ),
      };
    case 'achievements':
      return { title: 'SPC Achievement Cabinet', body: <AchievementCabinet /> };
    case 'myAccount':
      return { title: 'My Account', body: <MyAccount {...ctx} /> };

    case 'newSession':
      return { title: 'Create session / event', body: <NewSession {...ctx} payload={payload} /> };
    case 'editSession':
      return { title: 'Manage session', body: <EditSession state={state} ctx={ctx} payload={payload} /> };
    case 'safetyCard':
      return { title: 'My Safety Card', body: <SafetyCardModal ctx={ctx} state={state} payload={payload} /> };
    case 'buyTickets': {
      const s = (state.sessions || []).find(x => x.id === payload?.sessionId);
      return { title: 'Buy tickets', body: s ? <BuyTickets ctx={ctx} s={s} /> : <div className="empty">Session not found.</div> };
    }
    case 'bookWithNote': {
      const s = (state.sessions || []).find(x => x.id === payload?.sessionId);
      return { title: 'Confirm your booking', body: s ? <BookWithNote ctx={ctx} s={s} /> : <div className="empty">Session not found.</div> };
    }
    case 'aiCoach':
      return { title: 'Ask Smartie', body: <AiCoachChat ctx={ctx} /> };
    case 'newListing':
      return { title: 'Sell kit', body: <NewListing {...ctx} /> };
    case 'editListing': {
      const l = state.listings.find(x => x.id === payload?.id);
      return { title: 'Edit listing', body: l ? <EditListing listing={l} ctx={ctx} /> : <div className="empty">Listing not found.</div> };
    }
    case 'listing': {
      const l = state.listings.find(x => x.id === payload.id);
      return { title: l ? l.title : 'Listing', body: l ? <ListingDetail listing={l} ctx={ctx} /> : <div className="empty">Listing not found.</div> };
    }
    case 'messages': {
      return { title: 'Messages', body: <MessagesInbox ctx={ctx} state={state} /> };
    }
    case 'newMessage':
      return { title: 'New message', body: <NewMessagePicker ctx={ctx} /> };
    case 'notificationPrefs':
      return { title: 'Push notification settings', body: <NotificationPrefsPanel ctx={ctx} state={state} /> };
    case 'thread': {
      const c = (state.conversations || []).find(x => x.id === payload.id);
      return { title: c ? `Chat with ${c.withName}` : 'Chat', body: c ? <Thread conv={c} ctx={ctx} /> : <div className="empty">Conversation not found.</div> };
    }
    case 'newDiscount':
      return { title: payload ? 'Edit member offer' : 'Add member offer', body: <NewDiscount {...ctx} editing={payload} /> };
    case 'newResource':
    case 'editResource':
      return { title: type === 'editResource' ? 'Edit resource' : 'Upload resource', body: <NewResource ctx={ctx} payload={payload} /> };
    case 'newRoute':
    case 'editRoute':
      return { title: type === 'editRoute' ? 'Edit route' : 'Add route', body: <NewRoute ctx={ctx} payload={payload} /> };
    case 'newReward':
      return { title: payload ? 'Edit SPC prize' : 'Add SPC prize', body: <NewReward {...ctx} editing={payload} /> };
    case 'iosInstall':
      return {
        title: 'Install SPC on your iPhone', body: (
          <div className="ios-install-steps" data-testid="ios-install-steps">
            <div className="notice">iOS doesn't let apps install themselves — it only takes two taps in Safari or Chrome.</div>
            <div className="list" style={{ marginTop: 12 }}>
              <div className="item"><span className="ios-step-num">1</span><div><strong>Tap the Share icon</strong><div className="meta">The square with an arrow, in your browser's toolbar</div></div><Share size={20} /></div>
              <div className="item"><span className="ios-step-num">2</span><div><strong>Choose “Add to Home Screen”</strong><div className="meta">Scroll down the share sheet if you don't see it</div></div><SquarePlus size={20} /></div>
              <div className="item"><span className="ios-step-num">3</span><div><strong>Tap “Add”</strong><div className="meta">SPC Clubhouse appears on your home screen like any app</div></div><CheckCircle2 size={20} /></div>
            </div>
          </div>
        )
      };

    case 'bookingConfirmed': {
      const many = payload?.sessions;
      const isBlock = Array.isArray(many) && many.length > 1;
      const list = isBlock ? many : (payload?.session ? [payload.session] : (many || []));
      const s = list[0] || {};
      const dateLabel = s.dateISO ? formatDisplay(s.dateISO) : s.date;
      return {
        title: "You're booked!", body: (
          <div className="booking-confirmed" data-testid="booking-confirmed-panel">
            <div className="booking-confirmed-icon"><PartyPopper size={30} /></div>
            <h3 style={{ margin: '10px 0 4px' }}>{isBlock ? `${list.length} weeks booked!` : (s.title || 'Session confirmed')}</h3>
            {isBlock ? (
              <div className="list" style={{ marginTop: 12, maxHeight: 180, overflowY: 'auto' }} data-testid="booking-confirmed-week-list">
                {list.map(w => (
                  <div className="item" key={w.id} style={{ padding: '6px 0' }}>
                    <div><strong>{w.dateISO ? formatDisplay(w.dateISO) : w.date}</strong><div className="meta">{w.time} · {w.location || 'TBC'}</div></div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}><Clock size={16} style={{ color: 'var(--spc-yellow)', flexShrink: 0 }} /><span><strong>{dateLabel} · {s.time}</strong></span></div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}><MapPin size={16} style={{ color: 'var(--spc-yellow)', flexShrink: 0 }} /><span>{s.location || 'TBC'}</span></div>
              </div>
            )}
            <p style={{ color: '#a4a8ad', fontSize: 13, marginTop: 14 }}>We'll send you a reminder pop-up and push notification 2 hours before it starts.</p>
            <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              {!isBlock && (
                <button className="btn secondary" data-testid="booking-confirmed-google-btn" onClick={() => window.open(googleCalendarUrl(s), '_blank')}>
                  <Calendar size={15} style={{ marginRight: 6, verticalAlign: '-2px' }} />Google Calendar
                </button>
              )}
              <button className="btn secondary" data-testid="booking-confirmed-ics-btn" onClick={() => downloadIcs(list, isBlock ? 'spc-block-booking.ics' : `spc-${s.id}.ics`)}>
                <Download size={15} style={{ marginRight: 6, verticalAlign: '-2px' }} />{isBlock ? `Download all ${list.length} (.ics)` : 'Add to Calendar (.ics)'}
              </button>
            </div>
            <button className="btn" data-testid="booking-confirmed-close-btn" style={{ marginTop: 10, width: '100%' }} onClick={ctx.closeModal}>Nice one</button>
          </div>
        )
      };
    }

    case 'sessionReminder':
      return {
        title: 'Starting soon!', body: (
          <div className="booking-confirmed" data-testid="session-reminder-panel">
            <div className="booking-confirmed-icon"><Clock size={30} /></div>
            <h3 style={{ margin: '10px 0 4px' }}>{payload?.title}</h3>
            <p style={{ color: '#c4c8cd', fontSize: 14 }}>{payload?.body}</p>
            <button className="btn" data-testid="session-reminder-close-btn" style={{ marginTop: 10, width: '100%' }} onClick={ctx.closeModal}>Got it</button>
          </div>
        )
      };

    case 'checkedIn':
      return {
        title: "You're checked in!", body: (
          <div className="booking-confirmed" data-testid="checked-in-panel">
            <div className="booking-confirmed-icon"><CheckCircle2 size={30} /></div>
            <h3 style={{ margin: '10px 0 4px' }}>{payload?.title || 'Checked in ✅'}</h3>
            <p style={{ color: '#c4c8cd', fontSize: 14 }}>{payload?.body}</p>
            <button className="btn" data-testid="checked-in-close-btn" style={{ marginTop: 10, width: '100%' }} onClick={ctx.closeModal}>Nice one</button>
          </div>
        )
      };

    case 'breakingNewsDetail':
      return {
        title: 'SPC Breaking News', body: (
          <div className="booking-confirmed" data-testid="breaking-news-detail-panel">
            <div className="booking-confirmed-icon"><Megaphone size={30} /></div>
            <h3 style={{ margin: '10px 0 4px' }}>{payload?.title}</h3>
            <p style={{ color: '#c4c8cd', fontSize: 14 }}>{payload?.body}</p>
            <button className="btn" data-testid="breaking-news-detail-close-btn" style={{ marginTop: 10, width: '100%' }} onClick={ctx.closeModal}>Got it</button>
          </div>
        )
      };

    case 'memberBilling':
      return {
        title: 'Membership & billing', body: (
          <>
            <div className="notice"><strong>{state.member.billingStatus}</strong> — {state.member.billingPlan}</div>
            <div className="list" style={{ marginTop: 12 }}>
              <div className="item"><div><strong>Current subscription</strong><div className="meta">{state.member.billingAmount || 'Active'} {state.member.nextPayment ? '· next payment ' + state.member.nextPayment : ''}</div></div><span className="pill green">{state.member.billingStatus}</span></div>
              <div className="item"><div><strong>Payment method</strong><div className="meta">{state.member.paymentMethod || 'Add a payment method'}</div></div><button className="btn secondary" onClick={() => toast('Email info@smartperformancecoaching.co.uk to update your card')}>Update</button></div>
              <div className="item"><div><strong>Invoices & receipts</strong><div className="meta">Email us for a copy of any receipt.</div></div><button className="btn secondary" onClick={() => toast('Email info@smartperformancecoaching.co.uk for receipts')}>Request</button></div>
            </div>
            <div className="notice">Card details are collected securely by Stripe and are never stored by SPC.</div>
          </>
        )
      };
    case 'changePlan': {
      const coaching = state.services.filter(s => s.category === 'Coaching' || s.title === 'SPC Plan+');
      return {
        title: 'Change SPC support', body: (
          <>
            <div className="notice">Chat to us and we’ll switch your support package for you — any credit or proration will be applied fairly.</div>
            <div className="list" style={{ marginTop: 12 }}>{coaching.map(s => <div className="item" key={s.id}><div><strong>{s.title}</strong><div className="meta">{s.price}</div></div><button className="btn secondary" onClick={() => toast('Email info@smartperformancecoaching.co.uk to switch package')}>Choose</button></div>)}</div>
          </>
        )
      };
    }

    case 'adminMembers':
      return { title: 'Members', body: <AdminMembers state={state} ctx={ctx} /> };
    case 'adminRenewals':
      return { title: 'Renewals — next 30 days', body: <AdminRenewals state={state} ctx={ctx} /> };
    case 'adminDiscountCodes':
      return { title: 'Discount codes', body: <AdminDiscountCodes state={state} ctx={ctx} /> };
    case 'adminReset':
      return { title: 'Danger zone — total clean sweep', body: <AdminReset ctx={ctx} /> };
    case 'adminLaunchReset':
      return { title: 'Go-live reset — keep bookings', body: <AdminLaunchReset ctx={ctx} /> };
    case 'adminCheckIn':
      return { title: 'Check in members', body: <AdminCheckIn state={state} ctx={ctx} /> };
    case 'adminChallenge':
      return { title: 'Club challenge', body: <AdminChallenge state={state} ctx={ctx} initialTab={payload?.tab} /> };
    case 'adminBreakingNews':
      return { title: 'SPC Breaking News', body: <AdminBreakingNews state={state} ctx={ctx} /> };
    case 'adminBroadcasts':
      return { title: 'Recent broadcasts', body: <AdminBroadcasts ctx={ctx} /> };
    case 'adminAuditLog':
      return { title: 'Recent admin actions', body: <AdminAuditLog ctx={ctx} /> };
    case 'adminSettings':
      return { title: 'Club settings', body: <AdminSettings state={state} ctx={ctx} /> };
    case 'adminAttendance':
      return { title: 'Attendance', body: <div className="list">{state.sessions.map(s => <div className="item" key={s.id}><div><strong>{s.title}</strong><div className="meta">{s.date} · {(s.attended ?? state.adminData.attendance[s.id]) || 0} attended · {s.booked} booked</div></div><div style={{ display: 'flex', gap: 6 }}><button className="btn" onClick={() => { ctx.closeModal(); ctx.openModal('adminCheckIn', { sid: s.id }); }} data-testid={`checkin-open-${s.id}`}>Check in</button><button className="btn secondary" onClick={() => { const now = (s.attended ?? state.adminData.attendance[s.id]) || 0; if (now >= s.booked) return toast('All booked members already marked'); ctx.markAttendance(s.id); }}>+1</button></div></div>)}</div> };
    case 'adminWaitlists':
      return { title: 'Waiting lists', body: <div className="list">{state.sessions.map(s => <div className="item" key={s.id}><div><strong>{s.title}</strong><div className="meta">{s.booked}/{s.capacity} booked · {state.adminData.waitlists[s.id] || 0} waiting</div></div><button className="btn secondary" disabled={(state.adminData.waitlists[s.id] || 0) <= 0 || s.booked >= s.capacity} onClick={() => ctx.promoteWaitlist(s.id)}>Promote one</button></div>)}</div> };
    case 'adminReminders':
      return { title: 'Automated reminders', body: <div className="list">{state.sessions.map(s => <div className="item" key={s.id}><div><strong>{s.title}</strong><div className="meta">{s.date} · Reminder {state.adminData.reminders[s.id] ? 'ON' : 'OFF'}</div></div><button className="btn secondary" onClick={() => ctx.toggleReminder(s.id)}>{state.adminData.reminders[s.id] ? 'Turn off' : 'Turn on'}</button></div>).concat(<button key="test" className="btn" onClick={() => toast('Reminder test sent to you')}>Send test reminder</button>)}</div> };
    case 'adminEventHistory': {
      const today = new Date().toISOString().slice(0, 10);
      const past = state.sessions.filter(s => s.dateISO && s.dateISO < today).sort((a, b) => (b.dateISO || '').localeCompare(a.dateISO || ''));
      return { title: 'Event history', body: !past.length
        ? <div className="empty" data-testid="admin-event-history-empty">No completed sessions yet — past sessions will show up here once they've happened.</div>
        : <div className="list">{past.map(s => <div className="item" key={s.id}><div><strong>{s.title}</strong><div className="meta">{s.date} · {(state.adminData.attendance[s.id] ?? s.attended ?? 0)}/{s.capacity} attended</div></div><span className="pill green">Completed</span></div>)}</div> };
    }
    case 'adminCoaches': {
      const rows = state.members.filter(m => m.role !== 'Admin');
      const roles = ['Member', 'Coach', 'Run Leader'];
      return { title: 'User permissions', body: (
        <>
          <div className="notice">Give members a role. <strong>Coach</strong> and <strong>Run Leader</strong> can create sessions and vote in weekly polls; <strong>Members</strong> can book, chat and earn points.</div>
          <div className="list">
            {rows.length === 0 ? <div className="empty">No members yet.</div> : rows.map(m => (
              <div className="item" key={m.id} data-testid={`user-perm-${m.id}`}>
                <div style={{ flex: 1 }}>
                  <strong>{m.name}</strong>
                  <div className="meta">{m.email}</div>
                </div>
                <select
                  className="perm-role"
                  value={m.role || 'Member'}
                  onChange={(e) => ctx.updateMember(m.id, { role: e.target.value })}
                  data-testid={`perm-role-${m.id}`}
                >
                  {roles.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                {(m.role === 'Coach' || m.role === 'Run Leader') && (
                  <button className="btn secondary" onClick={() => ctx.toggleCoachPermission(m.id)} data-testid={`perm-create-${m.id}`}>
                    {m.canCreate ? 'Can create sessions' : 'Cannot create sessions'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )};
    }
    case 'adminChallengeResults':
    case 'adminChallengeResults': {
      const results = state.members.filter(m => m.role !== 'Admin' && (m.challengeProgress || 0) > 0).sort((a, b) => (b.challengeProgress || 0) - (a.challengeProgress || 0)).slice(0, 20);
      return { title: 'Challenge results', body: <>
        <div className="notice"><strong>{state.challenge.title}</strong><br />Target: {state.challenge.target} {state.challenge.unit}</div>
        <div className="list" style={{ marginTop: 10 }}>
          {results.length ? results.map((r, i) => <div className="item" key={r.id}><div><strong>#{i + 1} {r.name}</strong></div><span className="pill yellow">{r.challengeProgress} {state.challenge.unit}</span></div>)
            : <div className="empty" data-testid="admin-challenge-results-empty">No progress logged yet for the current challenge.</div>}
        </div>
      </> };
    }
    case 'adminPoints':
      return { title: 'SPC Points ledger', body: <AdminPoints {...ctx} /> };
    case 'adminRedemptions':
      return { title: 'Reward redemptions', body: <div className="list">{state.adminData.redemptions.map(r => <div className="item" key={r.id}><div><strong>{r.name}</strong><div className="meta">{r.reward} · {r.points} pts</div></div><button className="btn secondary" onClick={() => ctx.fulfilRedemption(r.id)}>{r.status}</button></div>)}</div> };
    case 'adminReferrals': {
      const referred = state.members.filter(m => m.role !== 'Admin' && m.referredBy).sort((a, b) => (b.memberSince || '').localeCompare(a.memberSince || ''));
      const topReferrers = state.members.filter(m => (m.successfulReferrals || 0) > 0).sort((a, b) => (b.successfulReferrals || 0) - (a.successfulReferrals || 0));
      return { title: 'Referral tracking', body: (
        <div>
          <div className="notice"><strong>How it works.</strong> Every member has a unique referral code shown on their Membership Card + Points page. When a new signup enters that code on the registration form, both the referrer's counter and their auto-unlock achievements tick up (+100 pts on the first referral, more badges as they hit 3, 5, 10).</div>

          {topReferrers.length > 0 && (
            <>
              <h4 style={{ margin: '18px 0 8px' }}>Top referrers</h4>
              <div className="list">
                {topReferrers.map(m => (
                  <div className="item" key={m.id} data-testid={`referrer-${m.id}`}>
                    <div><strong>{m.name}</strong><div className="meta">Code: {m.referralCode}</div></div>
                    <span className="pill green">{m.successfulReferrals} referred</span>
                  </div>
                ))}
              </div>
            </>
          )}

          <h4 style={{ margin: '18px 0 8px' }}>Recent referred sign-ups</h4>
          {referred.length === 0 ? (
            <div className="empty" data-testid="referrals-empty">No referred sign-ups yet — share codes and they'll appear here.</div>
          ) : (
            <div className="list">
              {referred.map(m => (
                <div className="item" key={m.id} data-testid={`referred-${m.id}`}>
                  <div>
                    <strong>{m.name}</strong>
                    <div className="meta">Invited by <strong>{m.referredBy?.name}</strong> ({m.referredBy?.code}) · {m.approvalStatus === 'approved' ? 'Active' : 'Pending payment/approval'}</div>
                  </div>
                  <span className={`pill ${m.approvalStatus === 'approved' ? 'green' : 'yellow'}`}>{m.approvalStatus === 'approved' ? 'Joined' : 'Pending'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) };
    }
    case 'adminReporting': {
      const totalBookings = state.sessions.reduce((a, s) => a + s.booked, 0);
      const attended = Object.values(state.adminData.attendance).reduce((a, b) => a + (+b || 0), 0);
      const redemptions = state.adminData.redemptions.length;
      const exportCsv = () => {
        const activeCount = state.members.filter(m => m.role !== 'Admin' && String(m.status || '').toLowerCase() === 'active').length;
        const rows = [['Metric', 'Value'], ['Active members', activeCount], ['Upcoming bookings', totalBookings], ['Waiting list places', Object.values(state.adminData.waitlists).reduce((a, b) => a + (+b || 0), 0)], ['Reward redemptions', redemptions]];
        const csv = rows.map(r => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'spc-admin-report.csv'; a.click(); URL.revokeObjectURL(a.href); toast('Report exported');
      };
      return {
        title: 'SPC reporting', body: (
          <>
            <div className="grid">
              <div className="card span-6"><div className="stat"><strong>{state.members.filter(m => m.role !== 'Admin' && String(m.status || '').toLowerCase() === 'active').length}</strong><span>active members</span></div></div>
              <div className="card span-6"><div className="stat"><strong>{totalBookings}</strong><span>upcoming bookings</span></div></div>
              <div className="card span-6"><div className="stat"><strong>{attended}</strong><span>attendance records</span></div></div>
              <div className="card span-6"><div className="stat"><strong>{redemptions}</strong><span>reward redemptions</span></div></div>
            </div>
            <button className="btn secondary" style={{ marginTop: 12 }} onClick={exportCsv}>Export CSV</button>
          </>
        )
      };
    }
    case 'adminBilling': {
      const members = state.members.filter(m => m.role === 'Member');
      return { title: 'Subscriptions & billing', body: <><div className="notice">Stripe is the source of truth for card payments. Open a member's record in your Stripe dashboard to apply credits, change package, pause or cancel.</div><div className="list" style={{ marginTop: 12 }}>{members.map(m => <div className="item" key={m.id}><div><strong>{m.name}</strong><div className="meta">{m.billingPlan || 'SPC Membership'} · {m.billingStatus || 'Active'} {m.billingAmount ? '· ' + m.billingAmount : ''}</div></div><a className="btn secondary" href="https://dashboard.stripe.com/customers" target="_blank" rel="noopener noreferrer">Open in Stripe</a></div>)}</div></> };
    }
    case 'adminAchievements':
      return { title: 'Achievement management', body: <AdminAchievements state={state} ctx={ctx} /> };
    case 'adminServices':
      return { title: 'SPC services catalogue', body: <AdminServices state={state} ctx={ctx} openSPC={openSPC} /> };
    case 'adminEmailTemplates':
      return { title: 'Email templates', body: <AdminEmailTemplates ctx={ctx} /> };

    default:
      return { title: 'SPC', body: <div className="empty">Nothing here.</div> };
  }
}

function AdminReset({ ctx }) {
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const go = async () => {
    setBusy(true);
    await ctx.resetClubData();
    setBusy(false);
    ctx.closeModal();
  };
  return (
    <>
      <div className="notice" style={{ borderColor: '#e5484d' }}>
        <strong>This permanently deletes ALL club data:</strong> every member and coach account, all bookings & sessions, kit listings, chats & messages, games, notifications, points balances & ledger, reward redemptions, event history, challenge results, rewards, offers and announcements.
        <br /><br />
        <strong>Kept:</strong> admin accounts (points reset to 0) and the achievement cabinet catalogue. This cannot be undone.
      </div>
      <Field label={'Type WIPE to confirm'}>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="WIPE" data-testid="reset-confirm-input" />
      </Field>
      <button className="btn" style={{ marginTop: 12, background: '#e5484d', color: '#fff' }} disabled={text !== 'WIPE' || busy} onClick={go} data-testid="reset-confirm-btn">
        {busy ? 'Wiping…' : 'Wipe everything — fresh start'}
      </button>
    </>
  );
}

function AdminLaunchReset({ ctx }) {
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const go = async () => {
    setBusy(true);
    await ctx.launchReset();
    setBusy(false);
    ctx.closeModal();
  };
  return (
    <>
      <div className="notice" style={{ borderColor: '#e5484d' }}>
        <strong>This permanently deletes all beta-testing data:</strong> every member/coach account (except admins), points balances & ledger, ranks history, achievements progress, kit listings, chats & messages, games, notifications, reward redemptions and announcements.
        <br /><br />
        <strong>Kept:</strong> your sessions/events schedule and its bookings, admin accounts, services and the achievement cabinet catalogue. This cannot be undone.
      </div>
      <Field label={'Type LAUNCH to confirm'}>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="LAUNCH" data-testid="launch-reset-confirm-input" />
      </Field>
      <button className="btn" style={{ marginTop: 12, background: '#e5484d', color: '#fff' }} disabled={text !== 'LAUNCH' || busy} onClick={go} data-testid="launch-reset-confirm-btn">
        {busy ? 'Resetting…' : 'Reset for launch — keep bookings'}
      </button>
    </>
  );
}

const ROLE_PERMS = [
  { key: 'create_sessions', label: 'Create & manage sessions' },
  { key: 'scan_checkins', label: 'Check in members' },
  { key: 'manage_members', label: 'Manage members & roles' },
  { key: 'view_polls', label: 'Access the Coaches / Leaders hub (weekly poll, safety cards, private resources)' },
];
const permLabel = (k) => (ROLE_PERMS.find(p => p.key === k) || {}).label || k;

function RoleEditor({ initial, onSave, onCancel, ctx }) {
  const [name, setName] = React.useState(initial?.name || '');
  const [perms, setPerms] = React.useState(new Set(initial?.permissions || []));
  const toggle = (k) => setPerms(prev => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const save = async () => {
    if (!name.trim()) { ctx.toast('Give the role a name'); return; }
    await onSave({ name: name.trim(), permissions: [...perms] });
  };
  return (
    <div className="role-editor" data-testid="role-editor">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Role name (e.g. Head Coach)" data-testid="role-name-input" />
      <div className="role-perm-grid">
        {ROLE_PERMS.map(p => (
          <label key={p.key} className={`role-perm ${perms.has(p.key) ? 'on' : ''}`} data-testid={`role-perm-${p.key}`}>
            <input type="checkbox" checked={perms.has(p.key)} onChange={() => toggle(p.key)} />
            <span>{p.label}</span>
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn" onClick={save} data-testid="role-save-btn">{initial ? 'Save role' : 'Create role'}</button>
        {onCancel && <button type="button" className="btn secondary" onClick={onCancel} data-testid="role-cancel-btn">Cancel</button>}
      </div>
    </div>
  );
}

function RolesManager({ state, ctx }) {
  const [open, setOpen] = React.useState(false);
  const [editId, setEditId] = React.useState(null);
  const roles = state.roles || [];
  return (
    <div className="roles-manager" data-testid="roles-manager">
      <div className="roles-manager-head">
        <div><strong>Roles &amp; permissions</strong><div className="meta">Build a role, tick what it can do, then assign it to members below.</div></div>
        <button type="button" className="btn secondary" onClick={() => { setOpen(o => !o); setEditId(null); }} data-testid="toggle-role-builder">{open ? 'Close' : '+ New role'}</button>
      </div>
      {open && !editId && (
        <RoleEditor ctx={ctx} onSave={async (p) => { const ok = await ctx.createRole(p); if (ok) setOpen(false); }} onCancel={() => setOpen(false)} />
      )}
      {roles.length > 0 && (
        <div className="roles-list" data-testid="roles-list">
          {roles.map(r => editId === r.id ? (
            <RoleEditor key={r.id} ctx={ctx} initial={r} onSave={async (p) => { const ok = await ctx.updateRole(r.id, p); if (ok) setEditId(null); }} onCancel={() => setEditId(null)} />
          ) : (
            <div className="role-row" key={r.id} data-testid={`role-row-${r.id}`}>
              <div style={{ minWidth: 0 }}>
                <strong>{r.name}</strong>
                <div className="role-chips">{(r.permissions || []).length ? r.permissions.map(k => <span key={k} className="pill" style={{ fontSize: 11 }}>{permLabel(k)}</span>) : <span className="meta">No permissions yet</span>}</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" className="btn secondary" style={{ padding: '5px 10px' }} onClick={() => { setEditId(r.id); setOpen(false); }} data-testid={`edit-role-${r.id}`}><Pencil size={13} /></button>
                <button type="button" className="btn secondary" style={{ padding: '5px 10px' }} onClick={() => ctx.deleteRole(r.id)} data-testid={`delete-role-${r.id}`}><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminRenewals({ state, ctx }) {
  const [sendingId, setSendingId] = React.useState(null);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const rows = state.members
    .filter(m => m.role !== 'Admin' && m.membershipRenewalDate)
    .map(m => {
      const renewal = new Date(m.membershipRenewalDate);
      const daysLeft = Math.round((renewal - today) / 86400000);
      return { ...m, daysLeft, renewal };
    })
    .filter(m => m.daysLeft <= 30)
    .sort((a, b) => a.daysLeft - b.daysLeft);
  const badge = (d) => d <= 0 ? 'red' : d <= 7 ? 'red' : d <= 14 ? 'yellow' : 'green';
  const label = (d) => d < 0 ? `${Math.abs(d)}d overdue` : d === 0 ? 'Due today' : `${d}d left`;
  const send = async (id) => { setSendingId(id); await ctx.sendRenewalReminder(id); setSendingId(null); };
  return (
    <div data-testid="admin-renewals-panel">
      <div className="notice" style={{ marginBottom: 12 }}>Members whose SPC membership renews within the next 30 days, soonest first.</div>
      {!rows.length ? (
        <div className="empty" data-testid="admin-renewals-empty">No renewals due in the next 30 days.</div>
      ) : (
        <div className="list">
          {rows.map(m => (
            <div className="item" key={m.id} data-testid={`admin-renewal-row-${m.id}`}>
              <div>
                <strong>{m.name}</strong>
                <div className="meta">
                  <span>{m.email}</span>
                  <span>Renews {m.renewal.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  <span>{m.hasStripeSubscription ? 'Stripe auto-renew' : m.billingStatus || 'Active'}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span className={`pill ${badge(m.daysLeft)}`} data-testid={`admin-renewal-badge-${m.id}`}>{label(m.daysLeft)}</span>
                {!m.hasStripeSubscription && (
                  <button className="btn secondary" disabled={sendingId === m.id} onClick={() => send(m.id)} data-testid={`admin-renewal-remind-${m.id}`}>
                    {sendingId === m.id ? 'Sending…' : 'Send reminder'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminDiscountCodes({ state, ctx }) {
  const [code, setCode] = React.useState('');
  const [percentOff, setPercentOff] = React.useState('100');
  const [busy, setBusy] = React.useState(false);
  const codes = state.discountCodes || [];

  const create = async (e) => {
    e.preventDefault();
    if (!code.trim() || !percentOff) return;
    setBusy(true);
    const ok = await ctx.createDiscountCode(code.trim(), percentOff);
    setBusy(false);
    if (ok) { setCode(''); setPercentOff('100'); }
  };

  return (
    <div data-testid="admin-discount-codes-panel">
      <div className="notice" style={{ marginBottom: 12 }}>Create a code members can enter at sign-up. A 100% code activates their membership instantly for free — no card needed. Any lower % applies as a discount on the £29/year Stripe checkout. Codes are multi-use until you turn them off.</div>
      <form className="form" onSubmit={create} style={{ marginBottom: 16 }}>
        <label>Code<input data-testid="new-discount-code-input" value={code} onChange={e => setCode(e.target.value)} placeholder="e.g. FREE2026" style={{ textTransform: 'uppercase' }} required /></label>
        <label>Discount % <span style={{ color: '#a4a8ad', fontWeight: 400, fontSize: 12 }}>(100 = free membership)</span>
          <input data-testid="new-discount-percent-input" type="number" min={1} max={100} value={percentOff} onChange={e => setPercentOff(e.target.value)} required />
        </label>
        <button className="btn" disabled={busy} data-testid="create-discount-code-btn">{busy ? 'Creating…' : 'Add code'}</button>
      </form>
      {!codes.length ? (
        <div className="empty" data-testid="admin-discount-codes-empty">No discount codes yet.</div>
      ) : (
        <div className="list">
          {codes.map(c => (
            <div className="item" key={c.code} data-testid={`admin-discount-code-row-${c.code}`}>
              <div>
                <strong>{c.code}</strong>
                <div className="meta">
                  <span>{c.percentOff}% off{c.percentOff >= 100 ? ' — free membership' : ''}</span>
                  <span>{c.usesCount} use{c.usesCount === 1 ? '' : 's'}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span className={`pill ${c.active ? 'green' : ''}`} data-testid={`admin-discount-code-status-${c.code}`}>{c.active ? 'Active' : 'Off'}</span>
                <button className="btn secondary" onClick={() => ctx.toggleDiscountCode(c.code)} data-testid={`toggle-discount-code-${c.code}`}>{c.active ? 'Turn off' : 'Turn on'}</button>
                <button className="btn ghost" onClick={() => ctx.deleteDiscountCode(c.code)} data-testid={`delete-discount-code-${c.code}`}><X size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function AdminMembers({ state, ctx }) {
  const [showForm, setShowForm] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [editRoleIds, setEditRoleIds] = React.useState([]);
  const [confirmId, setConfirmId] = React.useState(null);
  const [pauseConfirmId, setPauseConfirmId] = React.useState(null);
  const [promoteId, setPromoteId] = React.useState(null);
  const [promoteText, setPromoteText] = React.useState('');
  const [welcomePack, setWelcomePack] = React.useState(null);
  const [importResult, setImportResult] = React.useState(null);
  const [importBusy, setImportBusy] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [savingEditId, setSavingEditId] = React.useState(null);
  const [promoting, setPromoting] = React.useState(false);
  const [sortBy, setSortBy] = React.useState('joinedDesc');
  const [search, setSearch] = React.useState('');
  const fileInputRef = React.useRef(null);
  const members = React.useMemo(() => {
    const list = state.members.filter(m => m.role !== 'Admin');
    const sorted = [...list];
    if (sortBy === 'alpha') sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    else if (sortBy === 'joinedAsc') sorted.sort((a, b) => (a.joinedAt || '').localeCompare(b.joinedAt || ''));
    else sorted.sort((a, b) => (b.joinedAt || '').localeCompare(a.joinedAt || ''));
    const q = search.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter(m => (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q));
  }, [state.members, sortBy, search]);
  const submitCreate = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    setCreating(true);
    const payload = {
      name: f.get('name'), email: f.get('email'), password: f.get('password'),
      membershipStart: f.get('membershipStart') || undefined, membershipRenewalDate: f.get('membershipRenewalDate') || undefined,
      canCreate: f.get('canCreate') === 'on', canScan: f.get('canScan') === 'on',
    };
    const resp = await ctx.createMember(payload);
    setCreating(false);
    if (resp && resp.welcomePack) {
      setWelcomePack(resp.welcomePack);
      setShowForm(false);
    } else if (resp) {
      setShowForm(false);
    }
  };
  const startEdit = (m) => {
    setEditingId(m.id);
    setEditRoleIds(m.roleIds || []);
    setConfirmId(null);
  };
  const toggleEditRole = (rid) => {
    setEditRoleIds(prev => prev.includes(rid) ? prev.filter(id => id !== rid) : [...prev, rid]);
  };
  const submitEdit = async (e, id) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const data = {
      name: f.get('name'), email: f.get('email'), roleIds: editRoleIds,
      membershipStart: f.get('membershipStart') || '', membershipRenewalDate: f.get('membershipRenewalDate') || '',
      canCreate: f.get('canCreate') === 'on', canScan: f.get('canScan') === 'on',
    };
    const pw = f.get('password');
    if (pw) data.password = pw;
    setSavingEditId(id);
    const ok = await ctx.updateMember(id, data);
    setSavingEditId(null);
    if (ok) setEditingId(null);
  };
  const confirmPromote = async (id) => {
    setPromoting(true);
    const ok = await ctx.promoteToAdmin(id);
    setPromoting(false);
    if (ok) { setPromoteId(null); setPromoteText(''); }
  };
  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (!lines.length) return [];
    const header = lines[0].toLowerCase().split(',').map(h => h.trim());
    const idx = { name: header.indexOf('name'), email: header.indexOf('email'), membershipStart: header.indexOf('membershipstart'), membershipRenewalDate: header.indexOf('membershiprenewaldate') };
    const dataLines = idx.name === -1 || idx.email === -1 ? lines : lines.slice(1);
    return dataLines.map(line => {
      const cols = line.split(',').map(c => c.trim());
      if (idx.name === -1 || idx.email === -1) {
        return { name: cols[0] || '', email: cols[1] || '', membershipStart: cols[2] || '', membershipRenewalDate: cols[3] || '' };
      }
      return { name: cols[idx.name] || '', email: cols[idx.email] || '', membershipStart: idx.membershipStart > -1 ? cols[idx.membershipStart] || '' : '', membershipRenewalDate: idx.membershipRenewalDate > -1 ? cols[idx.membershipRenewalDate] || '' : '' };
    }).filter(r => r.name && r.email);
  };
  const onImportFileSelected = async (file) => {
    if (!file) return;
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await ctx.bulkImportMembersFile(fd);
      if (resp && resp.importResult) setImportResult(resp.importResult);
    } catch (e) { ctx.toast(errMsg(e) || 'Could not import that file'); }
    setImportBusy(false);
  };
  const [exporting, setExporting] = React.useState(false);
  const exportToExcel = async () => {
    setExporting(true);
    try {
      const ids = members.map(m => m.id).join(',');
      const { data } = await api.get('/admin/members/export', { params: ids ? { ids } : {}, responseType: 'blob' });
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `spc-members-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      ctx.toast(`Exported ${members.length} member${members.length === 1 ? '' : 's'} to Excel`);
    } catch (e) { ctx.toast(errMsg(e) || 'Could not export members'); }
    setExporting(false);
  };
  return (
    <>
      <RolesManager state={state} ctx={ctx} />
      <div className="notice" style={{ marginBottom: 14 }}>
        <strong>Adding existing members?</strong> Upload a CSV or Excel file with columns for name, email and expiry/renewal date (one row per member) — headers like <code>Name, Email, Expiry Date</code> are matched automatically. Accounts are created instantly Active with no password set and <strong>no email is sent</strong> — tell members to use "Forgot password?" on the sign-in page to set their own.
      </div>
      {showForm ? (
        <form className="form" onSubmit={submitCreate} data-testid="create-member-form" style={{ marginBottom: 14 }}>
          <Field label="Full name"><input name="name" required placeholder="e.g. Jane Runner" data-testid="create-member-name" /></Field>
          <Field label="Email"><input name="email" type="email" required placeholder="member@email.com" data-testid="create-member-email" /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Password"><input name="password" required minLength={6} placeholder="Min 6 characters" data-testid="create-member-password" /></Field>
            <Field label="Membership start (optional)"><input name="membershipStart" type="date" data-testid="create-member-membership-start" /></Field>
          </div>
          <div className="notice" data-testid="create-member-access">
            <strong>Access for this member:</strong>
            <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
              <label className="repeat-row" style={{ margin: 0 }}><input type="checkbox" name="canCreate" data-testid="create-can-create" /><span>Add & manage sessions</span></label>
              <label className="repeat-row" style={{ margin: 0 }}><input type="checkbox" name="canScan" data-testid="create-can-scan" /><span>Check in members</span></label>
            </div>
          </div>
          <Field label="Renewal date (optional)"><input name="membershipRenewalDate" type="date" data-testid="create-member-renewal-date" /></Field>
          <div className="notice">Created accounts are instantly Active — no payment or approval needed. Share the email + password with the member. Assign custom roles (Coach, Run Leader, etc.) after creating them, via Edit.</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" disabled={creating} data-testid="create-member-submit">{creating ? 'Creating…' : 'Create member'}</button>
            <button type="button" className="btn secondary" onClick={() => setShowForm(false)} data-testid="create-member-cancel">Cancel</button>
          </div>
        </form>
      ) : (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <button className="btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }} onClick={() => setShowForm(true)} data-testid="add-member-btn"><UserPlus size={15} /> Add member</button>
          <button className="btn secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }} onClick={() => fileInputRef.current?.click()} disabled={importBusy} data-testid="bulk-import-btn">
            <Upload size={15} /> {importBusy ? 'Importing…' : 'Bulk import (CSV/Excel)'}
          </button>
          <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls,text/csv" style={{ display: 'none' }} onChange={(e) => { onImportFileSelected(e.target.files?.[0]); e.target.value = ''; }} data-testid="bulk-import-file-input" />
        </div>
      )}
      {importResult && (
        <div className="notice" data-testid="bulk-import-result" style={{ marginBottom: 14 }}>
          <strong>Import complete:</strong> {importResult.created.length} created, {importResult.updated.length} updated{importResult.errors.length ? `, ${importResult.errors.length} skipped` : ''}.
          {importResult.errors.length > 0 && <div style={{ marginTop: 6 }}>{importResult.errors.map((e, i) => <div key={i} style={{ fontSize: 12, color: 'var(--muted)' }}>{e.email || '(blank)'}: {e.reason}</div>)}</div>}
          <div style={{ marginTop: 8 }}><button type="button" className="btn secondary" onClick={() => setImportResult(null)} data-testid="bulk-import-dismiss">Dismiss</button></div>
        </div>
      )}
      <div className="list" data-testid="admin-member-list">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}
            onClick={exportToExcel}
            disabled={exporting || !members.length}
            data-testid="export-members-excel-btn"
            title={search.trim() ? 'Export the current filtered list to Excel' : 'Export all members to Excel'}
          >
            <Download size={15} /> {exporting ? 'Exporting…' : `Export to Excel (${members.length})`}
          </button>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search members by name or email…"
            data-testid="member-search-input"
            style={{ flex: '1 1 220px', minWidth: 180 }}
          />
          <label style={{ fontSize: 12.5, color: 'var(--muted)', margin: 0 }}>Sort by</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="member-sort-select" style={{ width: 'auto' }}>
            <option value="joinedDesc">Date joined (newest first)</option>
            <option value="joinedAsc">Date joined (oldest first)</option>
            <option value="alpha">Alphabetical (A–Z)</option>
          </select>
        </div>
        {search.trim() && (
          <div className="meta" data-testid="member-search-count" style={{ marginBottom: 10 }}>
            {members.length} member{members.length === 1 ? '' : 's'} match "{search.trim()}"
          </div>
        )}
        {members.length ? members.map(m => editingId === m.id ? (
          <form className="form item" key={m.id} onSubmit={(e) => submitEdit(e, m.id)} style={{ display: 'block' }} data-testid={`edit-member-form-${m.id}`}>
            <Field label="Full name"><input name="name" defaultValue={m.name} required data-testid="edit-member-name" /></Field>
            <Field label="Email"><input name="email" type="email" defaultValue={m.email} required data-testid="edit-member-email" /></Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="New password (optional)"><input name="password" placeholder="Leave blank to keep" data-testid="edit-member-password" /></Field>
              <Field label="Membership start"><input name="membershipStart" type="date" defaultValue={m.membershipStart || ''} data-testid="edit-member-membership-start" /></Field>
            </div>
            <div className="notice" data-testid={`edit-member-access-${m.id}`}>
              <strong>Access for this member:</strong> may need to add sessions or check members in — grant what applies.
              <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                <label className="repeat-row" style={{ margin: 0 }}><input type="checkbox" name="canCreate" defaultChecked={m.canCreate} data-testid={`edit-can-create-${m.id}`} /><span>Add & manage sessions</span></label>
                <label className="repeat-row" style={{ margin: 0 }}><input type="checkbox" name="canScan" defaultChecked={m.canScan} data-testid={`edit-can-scan-${m.id}`} /><span>Check in members</span></label>
              </div>
            </div>
            {(state.roles || []).length > 0 && (
              <div className="notice" data-testid={`edit-member-custom-roles-${m.id}`}>
                <strong>Custom roles:</strong> tick any that apply (Coach, Run Leader, Committee, etc.) — a member can hold several at once.
                <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                  {state.roles.map(r => (
                    <label key={r.id} className="repeat-row" style={{ margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={editRoleIds.includes(r.id)}
                        onChange={() => toggleEditRole(r.id)}
                        data-testid={`edit-custom-role-${m.id}-${r.id}`}
                      /><span>{r.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <Field label="Renewal date"><input name="membershipRenewalDate" type="date" defaultValue={m.membershipRenewalDate || ''} data-testid="edit-member-renewal-date" /></Field>
            {m.hasStripeSubscription && <div className="notice">This member pays via Stripe — Stripe manages their renewal date automatically.</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button className="btn" disabled={savingEditId === m.id} data-testid="edit-member-save">{savingEditId === m.id ? 'Saving…' : 'Save changes'}</button>
              <button type="button" className="btn secondary" onClick={() => setEditingId(null)} data-testid="edit-member-cancel">Cancel</button>
            </div>
          </form>
        ) : (
          <div className="item" key={m.id} data-testid={`member-row-${m.id}`}>
            <div><strong>{m.name}</strong><div className="meta"><span>{m.email}</span><span className="pill" style={{ padding: '2px 8px', fontSize: 11 }}>{m.role || 'Member'}</span>{(m.roleNames || []).map(rn => <RoleBadgePill key={rn} roleName={rn} achievementCatalogue={state.achievementCatalogue} memberId={m.id} />)}<span className={`approval-pill ${m.approvalStatus === 'approved' ? 'approved' : 'pending'}`}>{m.approvalStatus === 'approved' ? 'Approved' : 'Awaiting payment'}</span></div>
              {(tenureLabel(m.joinedAt) || m.nextPayment) && (
                <div className="meta" data-testid={`member-tenure-${m.id}`} style={{ marginTop: 3 }}>
                  {tenureLabel(m.joinedAt) && <span>{tenureLabel(m.joinedAt)}</span>}
                  {m.nextPayment && <span>Renews {m.nextPayment}{!m.hasStripeSubscription ? ' (manual)' : ''}</span>}
                </div>
              )}
              <div className="meta" data-testid={`member-last-login-${m.id}`} style={{ marginTop: 3 }}><span>{lastLoginLabel(m.lastSeen || m.lastLogin)}</span></div>
            </div>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
              {m.approvalStatus !== 'approved' && <button className="btn" onClick={() => ctx.approveMember(m.id)} data-testid={`approve-member-${m.id}`}>Approve</button>}
              {m.approvalStatus === 'approved' && m.status === 'Active' && (
                pauseConfirmId === m.id
                  ? <button className="btn" style={{ background: '#e5484d', color: '#fff' }} onClick={() => { ctx.toggleMemberStatus(m.id); setPauseConfirmId(null); }} data-testid={`confirm-pause-member-${m.id}`}>Confirm pause?</button>
                  : <button className="btn secondary" title="Blocks this member's app access until reactivated" onClick={() => setPauseConfirmId(m.id)} data-testid={`toggle-member-${m.id}`}>Pause access</button>
              )}
              {m.approvalStatus === 'approved' && m.status !== 'Active' && <button className="btn" onClick={() => ctx.toggleMemberStatus(m.id)} data-testid={`toggle-member-${m.id}`}>Reactivate</button>}
              <button className="btn secondary" title="Grant full admin rights" onClick={() => { setPromoteId(m.id); setPromoteText(''); setConfirmId(null); }} data-testid={`promote-admin-${m.id}`}><ShieldCheck size={14} /></button>
              <button className="btn secondary" title="Edit member" onClick={() => startEdit(m)} data-testid={`edit-member-${m.id}`}><Pencil size={14} /></button>
              {confirmId === m.id
                ? <button className="btn" style={{ background: '#e5484d', color: '#fff' }} onClick={() => { ctx.deleteMember(m.id); setConfirmId(null); }} data-testid={`confirm-delete-member-${m.id}`}>Confirm delete?</button>
                : <button className="btn secondary" title="Delete member" onClick={() => setConfirmId(m.id)} data-testid={`delete-member-${m.id}`}><Trash2 size={14} /></button>}
            </div>
            {promoteId === m.id && (
              <div className="notice" style={{ borderColor: '#e5484d', width: '100%', marginTop: 10 }} data-testid={`promote-panel-${m.id}`}>
                <strong>Grant {m.name} full admin rights?</strong> They will have the exact same access as any other admin — full control over members, sessions, payments and club settings. This cannot be undone from here.
                <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input value={promoteText} onChange={(e) => setPromoteText(e.target.value)} placeholder="Type ADMIN to confirm" style={{ flex: 1, minWidth: 160 }} data-testid={`promote-confirm-input-${m.id}`} />
                  <button className="btn" style={{ background: '#e5484d', color: '#fff' }} disabled={promoteText !== 'ADMIN' || promoting} onClick={() => confirmPromote(m.id)} data-testid={`promote-confirm-btn-${m.id}`}>{promoting ? 'Promoting…' : 'Make full admin'}</button>
                  <button className="btn secondary" onClick={() => { setPromoteId(null); setPromoteText(''); }} data-testid={`promote-cancel-${m.id}`}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )) : <div className="empty">No members yet. Use “Add member” to create accounts, or members can sign up & pay themselves.</div>}
      </div>
      {welcomePack && <WelcomePackOverlay pack={welcomePack} onClose={() => setWelcomePack(null)} />}
    </>
  );
}

function WelcomePackOverlay({ pack, onClose }) {
  const [copied, setCopied] = React.useState(null);
  const loginUrl = window.location.origin;
  const message = `Hi ${pack.name.split(' ')[0]}! 👋\n\nYou're now a Smart Performance Coaching member — welcome aboard!\n\nHere are your Clubhouse login details:\n\nLogin: ${loginUrl}\nEmail: ${pack.email}\nPassword: ${pack.password}\n\nOn your first login you'll be walked through the app. Any questions, just shout.\n\n— Smart Performance Coaching`;

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1600);
    } catch (e) { /* ignore */ }
  };

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop open" data-testid="welcome-pack-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal welcome-pack" onMouseDown={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-head">
          <div>
            <div className="eyebrow" style={{ color: 'var(--spc-yellow)' }}>Welcome pack</div>
            <h3 style={{ margin: 0 }}>{pack.name} is in! 🎉</h3>
          </div>
          <button className="close" onClick={onClose} data-testid="welcome-pack-close">×</button>
        </div>
        <p style={{ marginTop: 4, color: '#c4c8cd' }}>
          Their account is active. Copy the message below and drop it into WhatsApp, SMS or email — they'll be up and running in under a minute.
        </p>

        <div className="welcome-pack-fields">
          <div className="wp-field">
            <span className="wp-label">Login</span>
            <div className="wp-value">
              <code>{loginUrl}</code>
              <button className="btn secondary wp-copy" onClick={() => copy(loginUrl, 'url')} data-testid="wp-copy-url">
                {copied === 'url' ? <><CheckCircle2 size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </button>
            </div>
          </div>
          <div className="wp-field">
            <span className="wp-label">Email</span>
            <div className="wp-value">
              <code>{pack.email}</code>
              <button className="btn secondary wp-copy" onClick={() => copy(pack.email, 'email')} data-testid="wp-copy-email">
                {copied === 'email' ? <><CheckCircle2 size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </button>
            </div>
          </div>
          <div className="wp-field">
            <span className="wp-label">Password</span>
            <div className="wp-value">
              <code data-testid="wp-password">{pack.password}</code>
              <button className="btn secondary wp-copy" onClick={() => copy(pack.password, 'password')} data-testid="wp-copy-password">
                {copied === 'password' ? <><CheckCircle2 size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </button>
            </div>
          </div>
        </div>

        <div className="wp-message-block">
          <div className="wp-label" style={{ marginBottom: 8 }}>Copy-paste welcome message</div>
          <pre className="wp-preview">{message}</pre>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button className="btn" onClick={() => copy(message, 'msg')} data-testid="wp-copy-message" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {copied === 'msg' ? <><CheckCircle2 size={15} /> Copied — paste it anywhere</> : <><Copy size={15} /> Copy welcome message</>}
            </button>
            <a
              className="btn secondary"
              href={`https://wa.me/?text=${encodeURIComponent(message)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              data-testid="wp-share-whatsapp"
            >
              <MessageCircle size={15} /> Share on WhatsApp
            </a>
          </div>
        </div>

        <div className="notice" style={{ marginTop: 14 }}>
          <strong>Heads up:</strong> this is the only time you'll see the password. If they lose it, edit their profile from the members list to set a new one.
        </div>
      </div>
    </div>
  );
}

// Square cover-photo picker with a compact side-by-side layout + zoom/crop step
function CoverPhotoPicker({ photo, setPhoto, toast, resizeMax = 1400, resizeQ = 0.8, label = 'Cover photo', hint, testidPrefix = 'cover-photo' }) {
  const [busy, setBusy] = React.useState(false);
  const [cropSrc, setCropSrc] = React.useState(null);
  const pick = async (files) => {
    const f = (files || [])[0];
    if (!f) return;
    setBusy(true);
    try { setCropSrc(await fileToResizedDataURL(f, resizeMax, resizeQ)); }
    catch (e) { toast && toast('Could not read that photo'); }
    setBusy(false);
  };
  const confirm = (dataUrl) => { setCropSrc(null); setPhoto(dataUrl); };
  return (
    <div className="cover-photo-field">
      <label>{label}</label>
      <div className="cover-photo-row">
        {photo
          ? <div className="cover-photo-square" data-testid={`${testidPrefix}-preview`}><img src={photo} alt="cover" /><button type="button" className="upl-x" onClick={() => setPhoto(null)} data-testid={`${testidPrefix}-remove`}><X size={13} /></button></div>
          : <label className="cover-photo-square drop" data-testid={`${testidPrefix}-btn`}><input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { pick(e.target.files); e.target.value = ''; }} /><Camera size={20} /><span>{busy ? '…' : 'Add'}</span></label>}
        <div className="cover-photo-info">
          <p>{hint || 'Square photos work best. After picking, drag to reposition and use the slider to zoom in for the best fit.'}</p>
          {photo && <label className="btn ghost" data-testid={`${testidPrefix}-change`}><input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { pick(e.target.files); e.target.value = ''; }} />{busy ? 'Processing…' : 'Change photo'}</label>}
        </div>
      </div>
      {cropSrc && <AvatarCrop src={cropSrc} shape="square" onConfirm={confirm} onCancel={() => setCropSrc(null)} />}
    </div>
  );
}

function NewSession({ addSession, closeModal, toast, payload }) {
  const dup = payload?.duplicateFrom || null;
  const [repeat, setRepeat] = React.useState(!!(dup && dup.recurring));
  const [type, setType] = React.useState(dup?.type || 'Run Session');
  const [blockBooking, setBlockBooking] = React.useState(!!dup?.blockBooking);
  const [photo, setPhoto] = React.useState(null);
  const [priceInput, setPriceInput] = React.useState(dup?.priceAmount != null ? String(dup.priceAmount) : '');
  const submit = (e) => { e.preventDefault(); const f = new FormData(e.target); const iso = f.get('dateISO'); const recurring = !!f.get('recurring'); const ptsRaw = f.get('points'); const points = ptsRaw === null || String(ptsRaw).trim() === '' ? null : Number(ptsRaw); const paRaw = f.get('priceAmount'); const priceAmount = paRaw === null || String(paRaw).trim() === '' ? null : Number(paRaw); addSession({ type: f.get('type'), title: f.get('title'), dateISO: iso, date: formatDisplay(iso) || iso, time: f.get('time'), location: f.get('location'), coach: f.get('coach'), capacity: f.get('capacity'), priceAmount, memberPaysFee: !!f.get('memberPaysFee'), duration: f.get('duration'), level: f.get('level'), description: f.get('description'), photo, points, recurring, occurrences: recurring ? Number(f.get('occurrences') || 8) : 1, blockBooking: recurring && f.get('type') === 'Classes' ? !!f.get('blockBooking') : false, paceGroups: f.get('type') === 'Run Session' ? !!f.get('paceGroups') : false, hasCatering: !!f.get('hasCatering') }); closeModal(); };
  return (
    <form className="form" onSubmit={submit} data-testid="new-session-form">
      {dup && <div className="notice" data-testid="duplicate-session-notice">Copied from "{dup.title}" — set the new date (and week count) below for the next block, everything else is pre-filled.</div>}
      <CoverPhotoPicker photo={photo} setPhoto={setPhoto} toast={toast} resizeMax={1400} label="Session photo (optional)" testidPrefix="session-photo" />
      <Field label="Type"><select name="type" value={type} onChange={(e) => setType(e.target.value)}><option>Run Session</option><option>Classes</option><option>Social</option><option>Workshop</option><option>Event</option><option>Other</option></select></Field>
      <Field label="Title"><input name="title" required defaultValue={dup?.title || ''} placeholder="e.g. Tuesday Run Club" /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label={repeat ? 'First date' : 'Date'}><input name="dateISO" type="date" required data-testid="new-session-date" /></Field>
        <Field label="Time"><input name="time" type="time" required defaultValue={dup?.time || ''} data-testid="new-session-time" /></Field>
      </div>
      <Field label="Location"><input name="location" required defaultValue={dup?.location || ''} placeholder="Holmer Park" /></Field>
      <Field label="Coach / host"><input name="coach" required defaultValue={dup?.coach || ''} placeholder="SPC Team" /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Capacity"><input name="capacity" type="number" min="1" defaultValue={dup?.capacity || 20} required /></Field>
        <Field label="Duration"><input name="duration" defaultValue={dup?.duration || ''} placeholder="60 min" /></Field>
      </div>
      <Field label={repeat && type === 'Classes' && blockBooking ? 'Price for the WHOLE block (£)' : 'Price (£, optional)'}>
        <input name="priceAmount" type="number" min="0" step="0.01" placeholder={repeat && type === 'Classes' && blockBooking ? 'Total for all weeks, e.g. 90' : '0 or blank = free'} value={priceInput} onChange={(e) => setPriceInput(e.target.value)} data-testid="session-price-amount" />
      </Field>
      {repeat && type === 'Classes' && blockBooking ? (
        <div className="notice">This is the TOTAL price for the whole block — members pay it once via a single Stripe checkout, not per week.</div>
      ) : (
        <div className="notice">If set, members pay by card via Stripe checkout before their spot is confirmed. Leave blank or 0 for free sessions.</div>
      )}
      {Number(priceInput) > 0 && (
        <label className="repeat-row" data-testid="session-fee-toggle">
          <input type="checkbox" name="memberPaysFee" defaultChecked={!!dup?.memberPaysFee} />
          <span>Member pays the card fee (~2% + 20p ≈ £{(Number(priceInput) * 0.02 + 0.20).toFixed(2)} on top) — otherwise the club absorbs it</span>
        </label>
      )}
      <Field label="Level"><input name="level" placeholder="All levels" defaultValue={dup?.level || 'All levels'} /></Field>
      <Field label="SPC Points on attendance (optional)"><input name="points" type="number" min="0" defaultValue={dup?.points ?? ''} placeholder="Leave blank to use the club default" data-testid="session-points-input" /></Field>
      <label className="repeat-row" data-testid="session-recurring-toggle">
        <input type="checkbox" name="recurring" checked={repeat} onChange={(e) => setRepeat(e.target.checked)} />
        <span><strong>Repeat weekly</strong><small>Create this session every week automatically — no need to re-add it.</small></span>
      </label>
      {repeat && (
        <Field label="Number of weeks"><input name="occurrences" type="number" min="2" max="52" defaultValue="8" data-testid="session-occurrences-input" /></Field>
      )}
      {repeat && type === 'Classes' && (
        <label className="repeat-row" data-testid="session-block-booking-toggle">
          <input type="checkbox" name="blockBooking" checked={blockBooking} onChange={(e) => setBlockBooking(e.target.checked)} />
          <span><strong>This is a block booking</strong><small>Members book & pay for the whole block in one go — no single-week booking option.</small></span>
        </label>
      )}
      {type === 'Run Session' && (
        <label className="repeat-row" data-testid="session-pace-groups-toggle">
          <input type="checkbox" name="paceGroups" defaultChecked={!!dup?.paceGroups} />
          <span><strong>Use pace groups</strong><small>Members pick a pace group (🔵 10:30-13:00 / 🟡 9:30-10:30) when booking.</small></span>
        </label>
      )}
      <label className="repeat-row" data-testid="session-has-catering-toggle">
        <input type="checkbox" name="hasCatering" defaultChecked={!!dup?.hasCatering} />
        <span><strong>This event includes food</strong><small>Members get asked for dietary requirements / allergies when booking.</small></span>
      </label>
      <Field label="Description"><textarea name="description" defaultValue={dup?.description || ''} placeholder="What is this session, who is it for, what should members expect?" /></Field>
      <button className="btn" data-testid="create-session-submit">{repeat ? 'Create weekly series' : 'Create session'}</button>
    </form>
  );
}

const REACTIONS = ['💛', '🔥', '👍'];
const REACTION_PICKER_WIDTH = 132;

function MessageReactionButton({ myReaction, onPick, isMe, testId }) {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState(null);
  const btnRef = React.useRef(null);

  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const left = Math.min(Math.max(12, r.left - REACTION_PICKER_WIDTH / 2 + r.width / 2), window.innerWidth - REACTION_PICKER_WIDTH - 12);
    const pickerHeight = 44;
    const fitsAbove = r.top - 10 - pickerHeight >= 12;
    const top = fitsAbove ? r.top - 10 - pickerHeight : r.bottom + 10;
    setPos({ top, left });
  };

  React.useEffect(() => {
    if (!open) return;
    const onOutside = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest('.reaction-picker-pop')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onOutside, true);
    document.addEventListener('touchstart', onOutside, true);
    return () => {
      document.removeEventListener('mousedown', onOutside, true);
      document.removeEventListener('touchstart', onOutside, true);
    };
  }, [open]);

  const pick = (emoji) => { onPick(emoji); setOpen(false); };

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        className={`like-msg-btn ${isMe ? 'me' : 'them'} ${myReaction ? 'active' : ''}`}
        data-testid={testId}
        aria-label={myReaction ? `You reacted ${myReaction} — tap to change` : 'React to message'}
        title={myReaction ? 'Tap to change your reaction' : 'Add a reaction'}
        onClick={(e) => { e.stopPropagation(); if (!open) place(); setOpen(v => !v); }}
      >
        <SmilePlus size={13} />
      </button>
      {open && pos && createPortal(
        <div className="reaction-picker-pop" data-testid={`${testId}-pop`} style={{ top: pos.top, left: pos.left, width: REACTION_PICKER_WIDTH }}>
          {REACTIONS.map(e => (
            <button key={e} type="button" className={`reaction-opt ${myReaction === e ? 'active' : ''}`} data-testid={`${testId}-opt-${e}`} title={myReaction === e ? 'Tap to remove' : undefined} onClick={() => pick(e)}>{e}</button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

function Thread({ conv, ctx }) {
  const [text, setText] = React.useState('');
  const [image, setImage] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const endRef = React.useRef(null);
  React.useEffect(() => { if (conv.unread) ctx.markConversationRead(conv.id); }, [conv.id]);
  React.useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [conv.messages.length]);
  const pickImage = async (files) => {
    const f = (files || [])[0];
    if (!f) return;
    setBusy(true);
    try { const url = await fileToResizedDataURL(f); setImage(url); } catch (e) { ctx.toast('Could not read that image'); }
    setBusy(false);
  };
  const send = (e) => {
    e.preventDefault();
    if (!text.trim() && !image) return;
    ctx.sendMessage(conv.id, text, image);
    setText(''); setImage(null);
  };
  return (
    <div className="thread">
      {conv.listingTitle && <div className="thread-note">Arranging: <strong>{conv.listingTitle}</strong></div>}
      <div className="thread-msgs" data-testid="thread-messages">
        {conv.messages.map((m, i) => {
          const isMe = m.from === 'me';
          const name = isMe ? (conv.meName || 'You') : (conv.withName || 'Member');
          const avatar = isMe ? conv.meAvatar : conv.withAvatar;
          const initials = name.split(' ').map(x => x[0]).slice(0, 2).join('').toUpperCase();
          const next = conv.messages[i + 1];
          const showAvatar = !next || next.from !== m.from;
          return (
            <div key={i} className={`msg-row ${isMe ? 'me' : 'them'}`}>
              <div className={`chat-avatar ${showAvatar ? '' : 'hide'}`} title={name} data-testid={`chat-avatar-${i}`}>
                {avatar ? <img src={avatar} alt={name} /> : <span>{initials}</span>}
              </div>
              <div className={`bubble ${isMe ? 'me' : 'them'}`} data-testid={`chat-bubble-${i}`} onDoubleClick={() => m.id && ctx.reactToMessage(conv.id, m.id, '💛')}>
                {m.image && <img className="bubble-img" data-testid={`chat-bubble-img-${i}`} src={m.image} alt="shared" onClick={() => window.open(m.image, '_blank')} />}
                {m.text && <span>{m.text}</span>}<em>{m.time}</em>
                {(m.myReaction || m.theirReaction) && (
                  <span
                    className={`bubble-heart ${isMe ? 'left' : 'right'} ${m.myReaction ? 'mine' : ''}`}
                    data-testid={`bubble-heart-${i}`}
                    title={m.myReaction ? 'Tap to remove your reaction' : undefined}
                    onClick={m.myReaction ? (e) => { e.stopPropagation(); ctx.reactToMessage(conv.id, m.id, m.myReaction); } : undefined}
                  >
                    {m.myReaction && m.theirReaction && m.myReaction !== m.theirReaction ? `${m.myReaction}${m.theirReaction}` : (m.myReaction || m.theirReaction)}
                  </span>
                )}
              </div>
              {m.id && (
                <MessageReactionButton
                  myReaction={m.myReaction}
                  isMe={isMe}
                  testId={`like-btn-${i}`}
                  onPick={(emoji) => ctx.reactToMessage(conv.id, m.id, emoji)}
                />
              )}
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      {image && (
        <div className="thread-preview">
          <img src={image} alt="preview" />
          <button type="button" className="upl-x" onClick={() => setImage(null)}><X size={12} /></button>
        </div>
      )}
      <form className="thread-input" onSubmit={send}>
        <label className="thread-photo" data-testid="chat-photo-btn">
          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { pickImage(e.target.files); e.target.value = ''; }} />
          <Camera size={18} />
        </label>
        <input data-testid="chat-message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder={busy ? 'Processing photo…' : `Message ${conv.withName}…`} />
        <button className="btn" type="submit" data-testid="chat-send-btn"><Send size={16} /></button>
      </form>
    </div>
  );
}

function AiCoachChat({ ctx }) {
  const [messages, setMessages] = React.useState([]);
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [loadingHistory, setLoadingHistory] = React.useState(true);
  const endRef = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;
    ctx.fetchCoachChatHistory().then((msgs) => { if (!cancelled) { setMessages(msgs || []); setLoadingHistory(false); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages.length, messages[messages.length - 1]?.text]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || busy) return;
    setText('');
    setMessages(prev => [...prev, { role: 'user', text: t }, { role: 'assistant', text: '' }]);
    setBusy(true);
    await ctx.streamCoachChat(t, (delta) => {
      setMessages(prev => {
        const next = [...prev];
        next[next.length - 1] = { role: 'assistant', text: (next[next.length - 1].text || '') + delta };
        return next;
      });
    });
    setBusy(false);
  };

  const clearChat = async () => {
    if (!window.confirm('Clear this conversation with Smartie?')) return;
    await ctx.clearCoachChat();
    setMessages([]);
  };

  return (
    <div className="thread ai-coach-chat" data-testid="ai-coach-chat">
      <div className="thread-note ai-coach-note">
        <span>Ask about bookings, membership, kit exchange, challenges & more — or ask for a tip based on your own stats.</span>
        {messages.length > 0 && <button type="button" className="ai-coach-clear-btn" onClick={clearChat} data-testid="ai-coach-clear-btn">Clear chat</button>}
      </div>
      <div className="thread-msgs" data-testid="ai-coach-messages">
        {loadingHistory && <div className="empty" style={{ padding: '20px 0' }}>Loading…</div>}
        {!loadingHistory && messages.length === 0 && (
          <div className="empty" data-testid="ai-coach-empty" style={{ padding: '20px 0' }}>
            Hi, I'm Smartie 👋 Ask me anything about the club, or say "give me a tip" for something personalized.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`msg-row ${m.role === 'user' ? 'me' : 'them'}`}>
            <div className={`chat-avatar coach-avatar-bot ${m.role === 'user' ? 'hide' : ''}`} title="Smartie">
              <Sparkles size={14} />
            </div>
            <div className={`bubble ${m.role === 'user' ? 'me' : 'them'}`} data-testid={`ai-coach-bubble-${i}`}>
              <span>{m.text || (busy && i === messages.length - 1 ? '…' : '')}</span>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form className="thread-input" onSubmit={send}>
        <input
          data-testid="ai-coach-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={busy ? 'Smartie is typing…' : 'Ask Smartie anything…'}
          disabled={busy}
          maxLength={1000}
        />
        <button className="btn" type="submit" data-testid="ai-coach-send-btn" disabled={busy || !text.trim()}><Send size={16} /></button>
      </form>
    </div>
  );
}


function NewListing({ addListing, closeModal, toast }) {
  const ctx = useSpc();
  const [photos, setPhotos] = React.useState([]);
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [category, setCategory] = React.useState('shoes');
  const [genBusy, setGenBusy] = React.useState(false);
  const submit = (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    addListing({ title, condition: f.get('condition'), price: formatListingPrice(f.get('price')), category, photos, description });
    closeModal();
  };
  const generateCaption = async () => {
    if (!photos.length) { toast('Add a photo first'); return; }
    setGenBusy(true);
    const caption = await ctx.generateListingCaption(photos[0], title, category);
    if (caption) setDescription(caption);
    setGenBusy(false);
  };
  return (
    <form className="form" onSubmit={submit}>
      <Field label="Item"><input name="title" required placeholder="e.g. Garmin Forerunner 255" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="listing-title-input" /></Field>
      <MultiImagePicker photos={photos} setPhotos={setPhotos} max={6} crop toast={toast} testidPrefix="listing-photo"
        label="Photos" hint="The first photo is the main ad image — tap “Set main” on any photo to promote it. Add up to 6." />
      <Field label="Description (optional)">
        <div className="ai-caption-wrap">
          <textarea name="description" rows={3} placeholder="Size, age, any wear & tear, why you're selling…" data-testid="listing-description-input" value={description} onChange={(e) => setDescription(e.target.value)} />
          <button type="button" className="ai-caption-btn" onClick={generateCaption} disabled={genBusy || !photos.length} data-testid="ai-generate-caption-btn" title="Write a caption from your photo using AI">
            <Sparkles size={13} /> {genBusy ? 'Writing…' : 'AI caption'}
          </button>
        </div>
      </Field>
      <Field label="Category"><select name="category" value={category} onChange={(e) => setCategory(e.target.value)}>{KIT_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label} — {c.hint}</option>)}</select></Field>
      <Field label="Condition"><select name="condition"><option>New</option><option>Nearly new</option><option>Good</option><option>Well used</option></select></Field>
      <Field label="Price (£)"><input name="price" type="number" min="0" step="0.01" required placeholder="0 = free" /></Field>
      <button className="btn">Post listing</button>
    </form>
  );
}

function ListingDetail({ listing, ctx }) {
  const [active, setActive] = React.useState(0);
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [offerOpen, setOfferOpen] = React.useState(false);
  const [offerAmount, setOfferAmount] = React.useState('');
  const [reduceOpen, setReduceOpen] = React.useState(false);
  const [reduceAmount, setReduceAmount] = React.useState('');
  const [photos, setPhotos] = React.useState(listing.photos || []);
  React.useEffect(() => {
    let cancelled = false;
    if ((listing.photoCount || 0) > (listing.photos || []).length) {
      ctx.fetchListingPhotos(listing.id).then((data) => { if (!cancelled && data?.photos) setPhotos(data.photos); });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing.id]);
  const cat = listing.category || 'other';
  const mine = ctx.user && listing.sellerId === ctx.user.id;
  const sold = listing.status === 'sold';
  const statusLabel = { reserved: 'Reserved', sold: 'Sold' }[listing.status];
  const sendOffer = () => {
    const amt = parseFloat(offerAmount);
    if (!amt || amt <= 0) { ctx.toast('Enter a valid offer amount'); return; }
    ctx.notifyInterest(listing, amt);
    ctx.closeModal();
  };
  const sendReduce = () => {
    const amt = parseFloat(reduceAmount);
    if (!amt || amt <= 0 || amt >= numericPriceValue(listing.price)) { ctx.toast('Enter a price lower than the current price'); return; }
    ctx.reduceListingPrice(listing.id, reduceAmount);
    setReduceOpen(false);
    setReduceAmount('');
  };
  return (
    <div>
      <div className="ld-main" onClick={() => photos.length && setLightboxOpen(true)} style={photos.length ? { cursor: 'zoom-in' } : undefined} data-testid="listing-detail-expand">
        {photos.length ? <img src={photos[active]} alt={listing.title} /> : <div className="ld-noimg"><Camera size={30} /><span>No photos on this listing</span></div>}
        {statusLabel && <span className={`status-chip ${listing.status}`}>{statusLabel}</span>}
      </div>
      {photos.length > 1 && (
        <div className="ld-thumbs">
          {photos.map((p, x) => <button key={x} className={x === active ? 'active' : ''} onClick={() => setActive(x)}><img src={p} alt="" /></button>)}
        </div>
      )}
      {lightboxOpen && <Lightbox photos={photos} index={active} setIndex={setActive} onClose={() => setLightboxOpen(false)} />}
      <div className="ld-info">
        <div className="bc-badges"><span className="pill">{listing.condition}</span><span className="pill soft">{kitCategoryLabel(cat)}</span></div>
        <h3 style={{ margin: '10px 0 4px' }}>{listing.title}</h3>
        {listing.oldPrice ? (
          <div className="price price-reduced" data-testid="listing-detail-price">
            <span className="price-was">{formatListingPrice(listing.oldPrice)}</span>
            <span className="price-now">{formatListingPrice(listing.price)}</span>
          </div>
        ) : (
          <div className="price" data-testid="listing-detail-price">{formatListingPrice(listing.price)}</div>
        )}
        <div className="meta">Listed by {listing.seller} · {listing.listedAgo}</div>
        {listing.interestCount > 0 && (
          <div className="pill soft" data-testid="listing-detail-interest-count" style={{ marginTop: 4 }}>
            <Flame size={11} /> {listing.interestCount} {listing.interestCount === 1 ? 'person' : 'people'} interested
          </div>
        )}
        {listing.description && <p style={{ marginTop: 10, color: 'var(--muted)' }} data-testid="listing-description-text">{listing.description}</p>}
      </div>
      {ctx.role === 'admin'
        ? <button className="btn danger" style={{ marginTop: 16 }} onClick={() => { ctx.removeListing(listing.id); ctx.closeModal(); }}>Remove listing</button>
        : mine
          ? (
            <div className="seller-controls" style={{ marginTop: 16 }}>
              <button className="btn ghost" data-testid="edit-listing-btn" onClick={() => ctx.openModal('editListing', { id: listing.id })}><Pencil size={15} /> Edit listing</button>
              {listing.status !== 'available' && <button className="btn ghost" onClick={() => ctx.setListingStatus(listing.id, 'available')}>Available</button>}
              {listing.status !== 'reserved' && <button className="btn secondary" onClick={() => ctx.setListingStatus(listing.id, 'reserved')}>Reserve</button>}
              {listing.status !== 'sold' && <button className="btn" onClick={() => ctx.setListingStatus(listing.id, 'sold')}>Mark sold</button>}
              {listing.status === 'available' && isNumericPrice(listing.price) && (
                reduceOpen ? (
                  <div className="offer-form" data-testid="reduce-price-form">
                    <span className="offer-currency">£</span>
                    <input type="number" min="0.01" step="0.01" max={numericPriceValue(listing.price)} placeholder="New price" value={reduceAmount}
                      data-testid="reduce-price-input" onChange={(e) => setReduceAmount(e.target.value)} />
                    <button className="btn secondary" data-testid="reduce-price-submit" onClick={sendReduce}>Save</button>
                  </div>
                ) : (
                  <button className="btn ghost" data-testid="reduce-price-btn" onClick={() => setReduceOpen(true)}><Tag size={15} /> Reduce price</button>
                )
              )}
            </div>
          )
          : (
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn" data-testid="im-interested-btn" disabled={sold} onClick={() => { ctx.notifyInterest(listing); ctx.closeModal(); }}><BellRing size={15} /> {sold ? 'Sold' : "I'm interested"}</button>
                {!sold && <button className="btn ghost" data-testid="make-offer-btn" onClick={() => setOfferOpen(v => !v)}><Tag size={15} /> Make an offer</button>}
              </div>
              {offerOpen && !sold && (
                <div className="offer-form" data-testid="offer-form">
                  <span className="offer-currency">£</span>
                  <input type="number" min="1" step="0.01" placeholder="Your offer" value={offerAmount} data-testid="offer-amount-input"
                    onChange={(e) => setOfferAmount(e.target.value)} />
                  <button className="btn secondary" data-testid="send-offer-btn" onClick={sendOffer}>Send offer</button>
                </div>
              )}
            </div>
          )}
    </div>
  );
}

function EditListing({ listing, ctx }) {
  const [photos, setPhotos] = React.useState(listing.photos || []);
  React.useEffect(() => {
    let cancelled = false;
    if ((listing.photoCount || 0) > (listing.photos || []).length) {
      ctx.fetchListingPhotos(listing.id).then((data) => { if (!cancelled && data?.photos) setPhotos(data.photos); });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing.id]);
  const submit = (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    ctx.editListing(listing.id, { title: f.get('title'), condition: f.get('condition'), category: f.get('category'), photos, description: f.get('description') });
    ctx.closeModal();
  };
  return (
    <form className="form" onSubmit={submit} data-testid="edit-listing-form">
      <Field label="Item"><input name="title" required defaultValue={listing.title} data-testid="edit-listing-title-input" /></Field>
      <Field label="Description (optional)"><textarea name="description" rows={3} defaultValue={listing.description} data-testid="edit-listing-description-input" /></Field>
      <MultiImagePicker photos={photos} setPhotos={setPhotos} max={6} crop toast={ctx.toast} testidPrefix="edit-listing-photo"
        label="Photos" hint="The first photo is the main ad image — tap “Set main” on any photo to promote it. Add up to 6." />
      <Field label="Category"><select name="category" defaultValue={listing.category || 'shoes'}>{KIT_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label} — {c.hint}</option>)}</select></Field>
      <Field label="Condition"><select name="condition" defaultValue={listing.condition}><option>New</option><option>Nearly new</option><option>Good</option><option>Well used</option></select></Field>
      <div className="notice">Price isn't editable here — use “Reduce price” on the listing to lower it.</div>
      <button className="btn" data-testid="save-listing-edit-btn">Save changes</button>
    </form>
  );
}

function TopicManager({ ctx, topics, setTopics }) {
  const [newTopic, setNewTopic] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const add = async () => {
    const name = newTopic.trim();
    if (!name) return;
    setBusy(true);
    const data = await ctx.addResourceTopic(name);
    setBusy(false);
    if (data) { setTopics(data.topics); setNewTopic(''); }
  };
  const remove = async (name) => {
    if (!window.confirm(`Remove the "${name}" topic? Resources already tagged with it will keep it.`)) return;
    const data = await ctx.removeResourceTopic(name);
    if (data) setTopics(data.topics);
  };

  return (
    <div className="notice" style={{ marginTop: -4, marginBottom: 4 }} data-testid="resource-topic-manager">
      <div style={{ fontWeight: 700, marginBottom: 8 }}>Manage topics</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {topics.map(t => (
          <span key={t} className="pill soft" style={{ gap: 6 }} data-testid={`resource-topic-chip-${t}`}>
            {t}
            <button type="button" onClick={() => remove(t)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, lineHeight: 1 }} data-testid={`resource-topic-remove-${t}`}>×</button>
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input value={newTopic} onChange={(e) => setNewTopic(e.target.value)} placeholder="New topic e.g. Tech &amp; Data Guides" style={{ flex: 1 }} data-testid="resource-new-topic-input" />
        <button type="button" className="btn secondary" disabled={busy} onClick={add} data-testid="resource-add-topic-btn">Add</button>
      </div>
    </div>
  );
}

function NewResource({ ctx, payload }) {
  const editing = payload?.resource || null;
  const onDone = payload?.onDone;
  const forcePrivate = !!payload?.forcePrivate;
  const [title, setTitle] = React.useState(editing?.title || '');
  const [description, setDescription] = React.useState(editing?.description || '');
  const [topics, setTopics] = React.useState([]);
  const [sport, setSport] = React.useState(editing?.sport || '');
  const [topic, setTopic] = React.useState(editing?.topic || '');
  const [format, setFormat] = React.useState(editing?.format || (forcePrivate ? 'image' : 'pdf'));
  const [videoMode, setVideoMode] = React.useState('upload');
  const [videoUrl, setVideoUrl] = React.useState(editing?.videoUrl || '');
  const [featured, setFeatured] = React.useState(!!editing?.featured);
  const [visibleTo, setVisibleTo] = React.useState(editing?.visibleTo || 'all');
  const [file, setFile] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    ctx.getResourceTopics().then(list => {
      setTopics(list);
      if (!sport && list.length) setSport(list[0]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !sport) { ctx.toast('Add a title and pick a topic'); return; }
    setBusy(true);
    if (editing) {
      await ctx.updateResource(editing.id, { title, description, sport, topic, format, videoUrl: format === 'video' ? videoUrl : '', featured, private: forcePrivate || !!editing.private, visibleTo: forcePrivate ? visibleTo : undefined });
    } else {
      const wantsLink = format === 'video' && videoMode === 'link';
      if (!file && !(wantsLink && videoUrl.trim())) { ctx.toast('Attach a file or paste a video link'); setBusy(false); return; }
      const fd = new FormData();
      fd.append('title', title);
      fd.append('description', description);
      fd.append('sport', sport);
      fd.append('topic', topic);
      fd.append('format', format);
      fd.append('featured', featured ? 'true' : 'false');
      fd.append('private', forcePrivate ? 'true' : 'false');
      if (forcePrivate) fd.append('visibleTo', visibleTo);
      if (wantsLink) fd.append('videoUrl', videoUrl.trim());
      if (file) fd.append('file', file);
      await ctx.uploadResource(fd);
    }
    setBusy(false);
    onDone && onDone();
    ctx.closeModal();
  };

  return (
    <form className="form" onSubmit={submit} data-testid="resource-form">
      {forcePrivate && <div className="notice" data-testid="resource-private-notice">🔒 This will only be visible in the Coaches / Leaders hub — never on the public Resource Library.</div>}
      {forcePrivate && (
        <Field label="Visible to">
          <select value={visibleTo} onChange={(e) => setVisibleTo(e.target.value)} data-testid="resource-visible-to-select">
            <option value="all">Everyone with Coach Hub access</option>
            <option value="coach">Coaches only</option>
            <option value="leader">Run Leaders only</option>
          </select>
        </Field>
      )}
      <TopicManager ctx={ctx} topics={topics} setTopics={setTopics} />
      <Field label="Title"><input value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="e.g. Base-building 8 week run plan" data-testid="resource-title-input" /></Field>
      <Field label="Description (optional)"><textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What's in it, who it's for…" data-testid="resource-description-input" /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Topic">
          <select value={sport} onChange={(e) => setSport(e.target.value)} data-testid="resource-sport-select">
            {topics.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Sub-topic (optional)"><input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Marathon, Strength" data-testid="resource-topic-input" /></Field>
      </div>
      <Field label="Format">
        <select value={format} disabled={!!editing} onChange={(e) => setFormat(e.target.value)} data-testid="resource-format-select">
          {FORMATS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
        </select>
      </Field>
      {!editing && format === 'video' && (
        <div className="segmented" role="tablist" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 6 }}>
          <button type="button" className={`btn ${videoMode === 'upload' ? '' : 'secondary'}`} onClick={() => setVideoMode('upload')} data-testid="resource-video-mode-upload">Upload file</button>
          <button type="button" className={`btn ${videoMode === 'link' ? '' : 'secondary'}`} onClick={() => setVideoMode('link')} data-testid="resource-video-mode-link">Paste link</button>
        </div>
      )}
      {editing && format === 'video' && (
        <Field label="Video link (leave blank if this is an uploaded file)"><input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://youtube.com/…" data-testid="resource-video-url-input" /></Field>
      )}
      {!editing && format === 'video' && videoMode === 'link' && (
        <Field label="YouTube / Vimeo link"><input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} required placeholder="https://youtube.com/…" data-testid="resource-video-url-input" /></Field>
      )}
      {!editing && (format !== 'video' || videoMode === 'upload') && (
        <Field label={format === 'video' ? 'Upload video (max 150MB — use a link above for bigger files)' : format === 'image' ? 'Upload image (max 10MB)' : 'Upload file (max 25MB)'}>
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="resource-file-input"
            accept={format === 'video' ? 'video/*' : format === 'image' ? 'image/*' : (format === 'pdf' || format === 'checklist') ? '.pdf' : format === 'doc' ? '.doc,.docx' : '.xls,.xlsx,.csv'} />
        </Field>
      )}
      {editing && <div className="notice">To replace the file itself, delete this resource and upload it again.</div>}
      <label className="item" style={{ marginTop: 4 }}>
        <div className="item-main">Feature this at the top of the library</div>
        <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} data-testid="resource-featured-checkbox" />
      </label>
      <button className="btn" disabled={busy} data-testid="resource-submit-btn">{busy ? 'Saving…' : editing ? 'Save changes' : 'Upload resource'}</button>
    </form>
  );
}

function NewRoute({ ctx, payload }) {
  const editing = payload?.route || null;
  const onDone = payload?.onDone;
  const [title, setTitle] = React.useState(editing?.title || '');
  const [description, setDescription] = React.useState(editing?.description || '');
  const [distance, setDistance] = React.useState(editing?.distance || '');
  const [elevation, setElevation] = React.useState(editing?.elevation || '');
  const [difficulty, setDifficulty] = React.useState(editing?.difficulty || '');
  const [startLocation, setStartLocation] = React.useState(editing?.startLocation || '');
  const [externalLink, setExternalLink] = React.useState(editing?.externalLink || '');
  const [photo, setPhoto] = React.useState(null);
  const [gpxFile, setGpxFile] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { ctx.toast('Add a title'); return; }
    setBusy(true);
    if (editing) {
      await ctx.updateRoute(editing.id, { title, description, distance, elevation, difficulty, startLocation, externalLink, photo: photo || undefined });
    } else {
      const fd = new FormData();
      fd.append('title', title);
      fd.append('description', description);
      fd.append('distance', distance);
      fd.append('elevation', elevation);
      fd.append('difficulty', difficulty);
      fd.append('startLocation', startLocation);
      fd.append('externalLink', externalLink);
      if (photo) fd.append('photo', photo);
      if (gpxFile) fd.append('gpx_file', gpxFile);
      await ctx.createRoute(fd);
    }
    setBusy(false);
    onDone && onDone();
    ctx.closeModal();
  };

  return (
    <form className="form" onSubmit={submit} data-testid="route-form">
      <div className="notice" data-testid="route-private-notice">🔒 Routes are only visible in the Coaches / Leaders hub.</div>
      <Field label="Title"><input value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="e.g. Riverside 10K loop" data-testid="route-title-input" /></Field>
      <Field label="Description (optional)"><textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Terrain, hazards, anything leaders should know…" data-testid="route-description-input" /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Field label="Distance"><input value={distance} onChange={(e) => setDistance(e.target.value)} placeholder="e.g. 10km" data-testid="route-distance-input" /></Field>
        <Field label="Elevation"><input value={elevation} onChange={(e) => setElevation(e.target.value)} placeholder="e.g. 150m" data-testid="route-elevation-input" /></Field>
        <Field label="Difficulty">
          <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} data-testid="route-difficulty-select">
            <option value="">—</option>
            <option value="Easy">Easy</option>
            <option value="Moderate">Moderate</option>
            <option value="Hard">Hard</option>
          </select>
        </Field>
      </div>
      <Field label="Start location"><input value={startLocation} onChange={(e) => setStartLocation(e.target.value)} placeholder="e.g. Holmer Park car park" data-testid="route-start-location-input" /></Field>
      <Field label="Strava / Komoot link (optional)"><input value={externalLink} onChange={(e) => setExternalLink(e.target.value)} placeholder="https://strava.com/routes/…" data-testid="route-external-link-input" /></Field>
      {editing?.hasPhoto && !photo && (
        <div className="sess-photo-preview" style={{ marginBottom: 4 }} data-testid="route-current-photo">
          <img src={ctx.routePhotoUrl(editing.id, editing.photoV)} alt={editing.title} />
          <div className="meta" style={{ marginTop: 4 }}>Current photo on file — upload a new one below to replace it.</div>
        </div>
      )}
      <CoverPhotoPicker photo={photo} setPhoto={setPhoto} toast={ctx.toast} resizeMax={1600} label="Route photo (optional)" testidPrefix="route-cover-photo" />
      {!editing && (
        <Field label="GPS file (optional — .gpx, .tcx or .kml)">
          <input type="file" accept=".gpx,.tcx,.kml" onChange={(e) => setGpxFile(e.target.files?.[0] || null)} data-testid="route-gpx-input" />
        </Field>
      )}
      {editing && editing.hasGpx && <div className="notice">GPS file on file: {editing.gpxFileName}. To replace it, delete this route and add it again.</div>}
      <button className="btn" disabled={busy} data-testid="route-submit-btn">{busy ? 'Saving…' : editing ? 'Save changes' : 'Add route'}</button>
    </form>
  );
}

function NewDiscount({ addDiscount, updateDiscount, closeModal, editing, toast }) {
  const [kind, setKind] = React.useState(editing?.kind === 'partner' ? 'partner' : 'discount');
  const [logo, setLogo] = React.useState(editing?.logo || null);
  const [photos, setPhotos] = React.useState(editing?.photos || []);
  const [busy, setBusy] = React.useState(false);
  const pickLogo = async (files) => {
    const f = (files || [])[0];
    if (!f) return;
    setBusy(true);
    try { const url = await fileToResizedDataURL(f, 240, 0.85); setLogo(url); }
    catch { /* ignore */ }
    setBusy(false);
  };
  const submit = (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const payload = {
      partner: f.get('partner'),
      offer: f.get('offer'),
      kind,
      code: kind === 'discount' ? (f.get('code') || '') : '',
      url: f.get('url') || '',
      logo: logo || '',
      photos,
    };
    if (kind === 'discount' && !payload.code) return;
    if (kind === 'partner' && !payload.url) return;
    if (editing) updateDiscount(editing.id, payload);
    else addDiscount(payload);
    closeModal();
  };
  return (
    <form className="form" onSubmit={submit} data-testid="new-discount-form">
      <div className="notice"><strong>Two kinds of offer:</strong> a <em>discount code</em> members can copy and use at checkout, or a <em>partner site</em> we link members straight to (affiliate deals, freebies, community perks).</div>
      <div className="segmented" role="tablist" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 6 }}>
        <button type="button" role="tab" aria-selected={kind === 'discount'} data-testid="discount-kind-code" onClick={() => setKind('discount')} className={`btn ${kind === 'discount' ? '' : 'secondary'}`}>Discount code</button>
        <button type="button" role="tab" aria-selected={kind === 'partner'} data-testid="discount-kind-partner" onClick={() => setKind('partner')} className={`btn ${kind === 'partner' ? '' : 'secondary'}`}>Partner / affiliate</button>
      </div>
      <div className="sess-photo-field">
        <label>Partner logo (optional — small square, shown next to the name)</label>
        {logo
          ? <div className="sess-photo-preview partner-logo-preview"><img src={logo} alt="logo" /><button type="button" className="upl-x" onClick={() => setLogo(null)} data-testid="discount-logo-remove"><X size={13} /></button></div>
          : <label className="sess-photo-drop" data-testid="discount-logo-btn"><input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { pickLogo(e.target.files); e.target.value = ''; }} /><Camera size={22} /><span>{busy ? 'Processing…' : 'Add logo'}</span></label>}
      </div>
      <MultiImagePicker photos={photos} setPhotos={setPhotos} max={4} label="Gallery photos (optional — shown as a swipeable gallery on the offer card)" toast={toast} testidPrefix="discount-gallery" />
      <Field label="Partner name"><input name="partner" required defaultValue={editing?.partner} placeholder="e.g. Runners Need, Trail Coffee Co." data-testid="discount-partner" /></Field>
      <Field label={kind === 'discount' ? 'Offer' : 'What members get'}><input name="offer" required defaultValue={editing?.offer} placeholder={kind === 'discount' ? '15% off all clothing' : 'Free coffee on first visit · SPC members club'} data-testid="discount-offer" /></Field>
      {kind === 'discount' ? (
        <>
          <Field label="Discount code"><input name="code" required defaultValue={editing?.code} placeholder="SPC15" data-testid="discount-code-input" style={{ textTransform: 'uppercase' }} /></Field>
          <Field label="Partner site (optional)"><input name="url" type="url" defaultValue={editing?.url} placeholder="https://partner.com/spc" data-testid="discount-url" /></Field>
        </>
      ) : (
        <Field label="Partner link"><input name="url" type="url" required defaultValue={editing?.url} placeholder="https://partner.com/spc-community" data-testid="discount-url" /></Field>
      )}
      <button className="btn" data-testid="discount-publish">{editing ? 'Save changes' : `Publish ${kind === 'discount' ? 'offer' : 'partner'}`}</button>
    </form>
  );
}

function NewReward({ addReward, updateReward, closeModal, editing, toast }) {
  const [photos, setPhotos] = React.useState(editing?.photos || []);
  const submit = (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const payload = { title: f.get('title'), cost: f.get('cost'), description: f.get('description'), photos };
    if (editing) updateReward(editing.id, payload);
    else addReward(payload);
    closeModal();
  };
  return (
    <form className="form" onSubmit={submit} data-testid="new-reward-form">
      <Field label="Prize title"><input name="title" required defaultValue={editing?.title} placeholder="e.g. £5 SPC Credit" data-testid="reward-title-input" /></Field>
      <Field label="Points cost"><input name="cost" type="number" min="1" required defaultValue={editing?.cost ?? 500} data-testid="reward-cost-input" /></Field>
      <Field label="Description"><textarea name="description" required defaultValue={editing?.description} placeholder="What can the member redeem this for?" data-testid="reward-description-input" /></Field>
      <MultiImagePicker photos={photos} setPhotos={setPhotos} max={4} label="Gallery photos (optional — shown as a swipeable gallery on the prize card)" toast={toast} testidPrefix="reward-gallery" />
      <button className="btn" data-testid="reward-publish">{editing ? 'Save changes' : 'Publish prize'}</button>
    </form>
  );
}

function AdminPoints({ state, manualPoints, updatePointEntry, deletePointEntry, user }) {
  const members = state.members.filter(m => m.id !== user?.id);
  const sortedMembers = [...state.members].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const [direction, setDirection] = React.useState('add');
  const [editingId, setEditingId] = React.useState(null);
  const [editDirection, setEditDirection] = React.useState('add');
  const [editDelta, setEditDelta] = React.useState('');
  const [editReason, setEditReason] = React.useState('');
  const [filterMemberId, setFilterMemberId] = React.useState('');
  const submit = (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const magnitude = Math.abs(Number(f.get('points')) || 0);
    const signed = direction === 'deduct' ? -magnitude : magnitude;
    manualPoints(f.get('member'), signed, f.get('reason'));
    e.target.reset();
    setDirection('add');
  };
  const startEdit = (x) => { setEditingId(x.id); setEditDirection(x.delta < 0 ? 'deduct' : 'add'); setEditDelta(String(Math.abs(x.delta))); setEditReason(x.reason || ''); };
  const cancelEdit = () => setEditingId(null);
  const saveEdit = async (id) => {
    const magnitude = Math.abs(Number(editDelta) || 0);
    const signed = editDirection === 'deduct' ? -magnitude : magnitude;
    const ok = await updatePointEntry(id, signed, editReason);
    if (ok) setEditingId(null);
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this ledger entry? The points will be reversed from the member\'s balance.')) return;
    await deletePointEntry(id);
  };
  const filterMember = filterMemberId ? state.members.find(m => m.id === filterMemberId) : null;
  const filteredLedger = filterMemberId ? state.adminData.pointLedger.filter(x => x.userId === filterMemberId) : state.adminData.pointLedger;
  return (
    <>
      <form className="form" onSubmit={submit} data-testid="admin-points-form">
        <Field label="Member"><select name="member" data-testid="admin-points-member-select">{members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</select></Field>
        <Field label="Points adjustment">
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className={`btn ${direction === 'add' ? '' : 'ghost'}`} onClick={() => setDirection('add')} data-testid="admin-points-direction-add">+ Add</button>
            <button type="button" className={`btn ${direction === 'deduct' ? '' : 'ghost'}`} onClick={() => setDirection('deduct')} data-testid="admin-points-direction-deduct">− Deduct</button>
            <input name="points" type="number" min="0" inputMode="numeric" required placeholder="e.g. 50" data-testid="admin-points-value-input" style={{ flex: 1, minWidth: 90 }} />
          </div>
        </Field>
        <Field label="Reason"><input name="reason" required placeholder="Reason for adjustment" data-testid="admin-points-reason-input" /></Field>
        <button className="btn" data-testid="admin-points-submit-btn">Add adjustment</button>
      </form>

      <Field label="View points history for">
        <select value={filterMemberId} onChange={(e) => setFilterMemberId(e.target.value)} data-testid="admin-points-filter-select">
          <option value="">All members</option>
          {sortedMembers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </Field>
      {filterMember && (
        <div className="notice" data-testid="admin-points-filter-summary">
          <strong>{filterMember.name}</strong> — current balance <strong>{filterMember.points}</strong> pts · lifetime earned <strong>{filterMember.lifetimePoints}</strong> pts · {filteredLedger.length} ledger {filteredLedger.length === 1 ? 'entry' : 'entries'}
        </div>
      )}

      <div className="list" style={{ marginTop: 14 }} data-testid="admin-points-ledger">
        {filteredLedger.length === 0 && <div className="empty" data-testid="admin-points-ledger-empty">No points history {filterMember ? `for ${filterMember.name} ` : ''}yet.</div>}
        {filteredLedger.map(x => editingId === x.id ? (
          <div className="item" key={x.id} data-testid={`ledger-editing-${x.id}`} style={{ flexWrap: 'wrap', gap: 8 }}>
            <div style={{ width: '100%', fontSize: 12, color: '#c4c8cd' }}><strong>{x.name}</strong> · {x.date}</div>
            <button type="button" className={`btn ${editDirection === 'add' ? '' : 'ghost'}`} onClick={() => setEditDirection('add')} data-testid={`ledger-edit-direction-add-${x.id}`}>+</button>
            <button type="button" className={`btn ${editDirection === 'deduct' ? '' : 'ghost'}`} onClick={() => setEditDirection('deduct')} data-testid={`ledger-edit-direction-deduct-${x.id}`}>−</button>
            <input type="number" min="0" inputMode="numeric" value={editDelta} onChange={(e) => setEditDelta(e.target.value)} style={{ width: 90 }} data-testid={`ledger-edit-delta-${x.id}`} />
            <input value={editReason} onChange={(e) => setEditReason(e.target.value)} style={{ flex: 1, minWidth: 160 }} data-testid={`ledger-edit-reason-${x.id}`} />
            <button type="button" className="btn" onClick={() => saveEdit(x.id)} data-testid={`ledger-save-${x.id}`}>Save</button>
            <button type="button" className="btn ghost" onClick={cancelEdit} data-testid={`ledger-cancel-${x.id}`}>Cancel</button>
          </div>
        ) : (
          <div className="item" key={x.id} data-testid={`ledger-row-${x.id}`}>
            <div><strong>{x.name}</strong><div className="meta">{x.reason} · {x.date}</div></div>
            <span className={`pill ${x.delta >= 0 ? 'yellow' : 'red'}`}>{x.delta >= 0 ? '+' : ''}{x.delta}</span>
            <button type="button" className="btn ghost" onClick={() => startEdit(x)} data-testid={`ledger-edit-${x.id}`}><Pencil size={13} /></button>
            <button type="button" className="btn ghost" onClick={() => remove(x.id)} data-testid={`ledger-delete-${x.id}`}><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
    </>
  );
}


const CHECKIN_LAST_SESSION_KEY = 'spc_checkin_last_session';

function AdminCheckIn({ state, ctx }) {
  // Only show today's and future sessions — once a session's date has passed, there's
  // nothing to check members into, so it's removed from the picker entirely.
  const cutoffISO = React.useMemo(() => new Date().toISOString().slice(0, 10), []);
  const checkinSessions = React.useMemo(
    () => state.sessions.filter(s => !s.dateISO || s.dateISO >= cutoffISO).sort((a, b) => (a.dateISO || '').localeCompare(b.dateISO || '')),
    [state.sessions, cutoffISO]
  );
  const [sid, setSidRaw] = React.useState(() => {
    const fromPayload = ctx.modal?.payload?.sid;
    if (fromPayload) return fromPayload;
    const saved = localStorage.getItem(CHECKIN_LAST_SESSION_KEY);
    if (saved && checkinSessions.some(s => s.id === saved)) return saved;
    return checkinSessions[0]?.id || '';
  });
  // Remembered across reopens of this tool, so picking today's session once is enough —
  // it won't reset back to some other default for the next member checked in.
  const setSid = (val) => { setSidRaw(val); localStorage.setItem(CHECKIN_LAST_SESSION_KEY, val); };
  React.useEffect(() => {
    if (sid && !checkinSessions.some(s => s.id === sid) && checkinSessions[0]?.id) {
      setSidRaw(checkinSessions[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkinSessions]);
  const [code, setCode] = React.useState('');
  const [lastResult, setLastResult] = React.useState(null);
  const [overlay, setOverlay] = React.useState(null);
  const [points, setPoints] = React.useState(state.settings?.attendancePoints ?? 25);
  const [savingPts, setSavingPts] = React.useState(false);
  const busyRef = React.useRef(false);
  const inputRef = React.useRef(null);
  const [checkinRoster, setCheckinRoster] = React.useState(null);

  React.useEffect(() => { inputRef.current?.focus(); }, []);

  React.useEffect(() => {
    let cancelled = false;
    setCheckinRoster(null);
    if (sid) ctx.fetchSessionRoster(sid).then((data) => { if (!cancelled && data) setCheckinRoster(data); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  React.useEffect(() => {
    if (!overlay) return;
    const t = setTimeout(() => setOverlay(null), 2000);
    return () => clearTimeout(t);
  }, [overlay]);

  const submitCode = async (e) => {
    e.preventDefault();
    const value = code.trim();
    if (!value || !sid || busyRef.current) return;
    busyRef.current = true;
    setCode('');
    try {
      const data = await ctx.scanAttendance(sid, value);
      if (data && (data.ok || data.already)) {
        const result = { name: data.memberName, already: !!data.already, pts: data.pts, firstIn: !!data.firstIn, bonusPts: data.bonusPts || 0, ts: Date.now() };
        setLastResult(result);
        setOverlay(result);
      }
    } finally {
      busyRef.current = false;
      inputRef.current?.focus();
    }
  };

  const savePoints = async () => {
    setSavingPts(true);
    await ctx.updateSettings({ attendancePoints: Number(points) });
    setSavingPts(false);
  };

  const removeAttendee = (m) => {
    if (!window.confirm(`Remove ${m.name} from this session's check-ins? Their points for this check-in will be reversed.`)) return;
    ctx.uncheckIn(sid, m.id);
    setLastResult(r => (r && r.name === m.name) ? null : r);
  };

  const currentPts = state.settings?.attendancePoints ?? 25;
  const pointsDirty = Number(points) !== Number(currentPts);
  const selectedSession = state.sessions.find(s => s.id === sid);
  const usingSessionOverride = (selectedSession?.points ?? null) !== null;
  const effectivePts = usingSessionOverride ? selectedSession.points : currentPts;

  return (
    <div className="form">
      {overlay && (
        <div className="checkin-success-backdrop" data-testid="checkin-success-overlay">
          <div className={`checkin-success-card ${overlay.already ? 'already' : 'ok'}`} data-testid="checkin-success-card">
            <div className="checkin-success-icon">{overlay.already ? '⚠️' : '✅'}</div>
            <h3>{overlay.name}</h3>
            <p>{overlay.already ? 'Already checked in' : 'Checked in successfully'}</p>
            {overlay.pts > 0 && <div className="checkin-success-pts">+{overlay.pts} pts</div>}
            {overlay.firstIn && <div className="checkin-first-in-badge" data-testid="checkin-first-in-badge">🎉 First one in! +{overlay.bonusPts} bonus</div>}
          </div>
        </div>
      )}

      <Field label="Session">
        <select value={sid} onChange={(e) => setSid(e.target.value)} data-testid="checkin-session-select">
          {checkinSessions.map(s => <option key={s.id} value={s.id}>{s.title} — {s.date}{s.points != null ? ` · ${s.points} pts` : ''}</option>)}
        </select>
      </Field>

      <form onSubmit={submitCode} className="checkin-code-form">
        <input
          ref={inputRef}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Member PIN or referral code"
          autoFocus
          disabled={!sid}
          className="checkin-code-input"
          data-testid="checkin-code-input"
        />
        <button className="btn" type="submit" disabled={!code.trim() || !sid} data-testid="checkin-submit-btn"><UserCheck size={16} /> Check in</button>
      </form>
      <div className="meta" style={{ marginTop: -8, marginBottom: 4 }}>Type or scan the member's 5-digit PIN (or referral code) and hit Enter — each check-in here awards <strong>+{effectivePts} pts</strong> (the first person checked in gets a <strong>+20 bonus</strong>).</div>

      {lastResult && (
        <div className={`solo-status ${lastResult.already ? 'draw' : 'win'}`} data-testid="checkin-last-result">
          {lastResult.already ? `${lastResult.name} was already checked in` : `✅ ${lastResult.name} checked in${lastResult.pts ? ` — +${lastResult.pts} pts` : ''}${lastResult.firstIn ? ' 🎉 first one in!' : ''}`}
        </div>
      )}

      {selectedSession?.attendedMembers?.length > 0 && (
        <div className="booked-members" data-testid="attended-members-list">
          <div className="booked-members-head">Checked in ({selectedSession.attendedMembers.length})</div>
          <div className="booked-members-rows">
            {(checkinRoster?.attendedMembers || selectedSession.attendedMembers).map(m => (
              <div key={m.id} className="booked-member-row" data-testid={`attended-member-${m.id}`}>
                {m.avatar ? <img src={m.avatar} alt={m.name} /> : <span className="booked-member-initial">{(m.name || 'M')[0].toUpperCase()}</span>}
                <span>{m.name || 'Member'}</span>
                <button type="button" className="btn ghost" style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: 11 }} onClick={() => removeAttendee(m)} data-testid={`uncheck-in-btn-${m.id}`}>Remove</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {state.role === 'admin' && (
      <div className="notice">
        <strong>Default points per check-in:</strong> every valid check-in awards this many SPC Points unless the session sets its own override.
        <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="number" min="0" value={points}
            onChange={(e) => setPoints(e.target.value)}
            style={{ width: 110 }}
            data-testid="checkin-points-input"
          />
          <span style={{ fontSize: 13, color: '#c4c8cd' }}>pts per check-in (default)</span>
          <button type="button" className="btn" disabled={!pointsDirty || savingPts} onClick={savePoints} data-testid="checkin-points-save-btn">{savingPts ? 'Saving…' : 'Save'}</button>
          {!pointsDirty && <span className="pill green" style={{ fontSize: 11 }}>Current: {currentPts} pts</span>}
        </div>
      </div>
      )}
    </div>
  );
}

function ChallengeLibraryPanel({ library, tierFilter, setTierFilter, applyTemplate, toggleHidden }) {
  const tiers = [
    { id: 'all', label: 'All', stars: '' },
    { id: 'easy', label: 'Easy', stars: '⭐' },
    { id: 'medium', label: 'Medium', stars: '⭐⭐' },
    { id: 'hard', label: 'Hard', stars: '⭐⭐⭐' },
    { id: 'epic', label: 'Epic', stars: '⭐⭐⭐⭐' },
  ];
  const filtered = library.filter(l => tierFilter === 'all' || l.tier === tierFilter);
  return (
    <div data-testid="challenge-library-panel">
      <div className="notice"><strong>{library.length} ready-made challenges</strong> from quick wins to epic stretch goals. Tap <strong>Use</strong> to prefill the New tab, or hide the ones that don't fit your club.</div>
      <div className="tier-filter" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {tiers.map(t => (
          <button key={t.id} className={`btn ${tierFilter === t.id ? '' : 'secondary'}`} onClick={() => setTierFilter(t.id)} data-testid={`tier-${t.id}`} style={{ padding: '6px 12px', fontSize: 12 }}>
            {t.stars} {t.label}
          </button>
        ))}
      </div>
      <div className="library-grid">
        {filtered.map(t => {
          const stars = { easy: '⭐', medium: '⭐⭐', hard: '⭐⭐⭐', epic: '⭐⭐⭐⭐' }[t.tier] || '';
          return (
            <div className={`library-card ${t.hidden ? 'is-hidden' : ''}`} key={t.title} data-testid={`lib-${t.title}`}>
              <div className="library-tier">{stars} {t.tier}</div>
              <h4>{t.title}</h4>
              <p>{t.description}</p>
              <div className="library-meta">
                <span>{t.target} {t.unit} · {t.period}</span>
                <span className="pill yellow">+{t.pointsAward}</span>
              </div>
              <div className="library-actions">
                <button className="btn" disabled={t.hidden} onClick={() => applyTemplate(t)} data-testid={`use-${t.title}`}>Use this</button>
                <button className="btn ghost" onClick={() => toggleHidden(t)} data-testid={`hide-${t.title}`}>{t.hidden ? 'Show' : 'Hide'}</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AdminServices({ state, ctx, openSPC }) {
  const [editing, setEditing] = React.useState(null); // service id or 'new'
  const [photoMap, setPhotoMap] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await ctx.getBenefits();
        if (!alive) return;
        setPhotoMap(Object.fromEntries((data.services || []).map(x => [x.id, x.photos || []])));
      } catch (e) { /* photos are a lazy enhancement — edit stays guarded below on failure */ }
    })();
    return () => { alive = false; };
  }, [ctx]);
  const services = state.services.map(s => (photoMap?.[s.id]?.length ? { ...s, photos: photoMap[s.id] } : s));
  const emptyForm = { title: '', category: '', price: '', description: '', link: '', photos: [] };
  const [form, setForm] = React.useState(emptyForm);
  const startEdit = (s) => { setEditing(s.id); setForm({ title: s.title || '', category: s.category || '', price: s.price || '', description: s.description || '', link: s.link || '', photos: s.photos || [] }); };
  const startNew = () => { setEditing('new'); setForm(emptyForm); };
  const setPhotos = (updater) => setForm(f => ({ ...f, photos: typeof updater === 'function' ? updater(f.photos || []) : updater }));
  const save = async () => {
    if (!form.title.trim()) return;
    const ok = editing === 'new' ? await ctx.addService(form) : await ctx.updateService(editing, form);
    if (ok) { setEditing(null); try { const { data } = await ctx.getBenefits(); setPhotoMap(Object.fromEntries((data.services || []).map(x => [x.id, x.photos || []]))); } catch (e) {} }
  };
  const remove = async (id) => {
    if (!window.confirm('Remove this service?')) return;
    await ctx.removeService(id);
  };
  return (
    <>
      <div className="notice">Add, edit or remove SPC services shown on the Services page. Tap Open to preview the link.</div>
      <div className="list" style={{ marginTop: 12 }}>
        {services.map(s => (
          <div className="item" key={s.id} data-testid={`svc-${s.id}`}>
            <div style={{ flex: 1 }}>
              <span className="pill yellow">{s.category}</span>
              <strong style={{ display: 'block', marginTop: 7 }}>{s.title}</strong>
              <div className="meta"><span>{s.price}</span><span>{s.description}</span></div>
            </div>
            <button className="btn secondary" onClick={() => openSPC(s.link)}>Open</button>
            <button className="btn secondary" onClick={() => startEdit(s)} disabled={photoMap === null} title={photoMap === null ? 'Loading photos…' : ''} data-testid={`svc-edit-${s.id}`}>{photoMap === null ? 'Loading…' : 'Edit'}</button>
            <button className="btn danger" onClick={() => remove(s.id)} data-testid={`svc-delete-${s.id}`}>Delete</button>
          </div>
        ))}
      </div>
      {editing ? (
        <div className="form" style={{ marginTop: 16 }}>
          <h4 style={{ margin: '0 0 8px' }}>{editing === 'new' ? 'Add a service' : 'Edit service'}</h4>
          {['title','category','price','description','link'].map(k => (
            <label key={k}>
              {k.charAt(0).toUpperCase() + k.slice(1)}
              <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`svc-field-${k}`} />
            </label>
          ))}
          <MultiImagePicker photos={form.photos} setPhotos={setPhotos} max={4} label="Gallery photos (optional — shown as a swipeable gallery on the service card)" toast={ctx.toast} testidPrefix="service-gallery" />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={save} data-testid="svc-save">Save</button>
            <button className="btn ghost" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn" style={{ marginTop: 12 }} onClick={startNew} data-testid="svc-new">+ Add a service</button>
      )}
    </>
  );
}

function ReviewSubmissionRow({ s, ctx }) {
  const [comment, setComment] = React.useState('');
  const [photos, setPhotos] = React.useState(s.photos && s.photos.length ? s.photos : (s.photo ? [s.photo] : []));
  React.useEffect(() => {
    let cancelled = false;
    if ((s.photoCount || 0) > photos.length) {
      ctx.fetchSubmissionPhotos(s.id).then((data) => { if (!cancelled && data?.photos) setPhotos(data.photos); });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.id]);
  return (
    <div className="item" data-testid={`review-submission-${s.id}`} style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <strong>{s.userName}</strong>
        <div className="meta"><span>{s.challengeTitle}</span></div>
        {s.note && <p style={{ marginTop: 6, fontSize: 14 }}>{s.note}</p>}
        {photos.map((p, i) => (
          <img key={i} src={p} alt="entry" style={{ marginTop: 8, marginRight: 6, maxWidth: 220, borderRadius: 12, border: '1px solid var(--line-strong)' }} />
        ))}
        <input placeholder="Note to the member (required if rejecting or sending back for changes)" value={comment} onChange={e => setComment(e.target.value)} data-testid={`review-comment-${s.id}`} style={{ marginTop: 8, width: '100%' }} />
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button className="btn" onClick={() => ctx.reviewSubmission(s.id, 'approved')} data-testid={`approve-sub-${s.id}`}>Approve</button>
        <button
          className="btn secondary"
          disabled={!comment.trim()}
          title={!comment.trim() ? 'Add a note first so the member knows what to fix' : ''}
          onClick={() => ctx.reviewSubmission(s.id, 'needs_changes', comment)}
          data-testid={`needs-changes-sub-${s.id}`}
        >
          Send back for changes
        </button>
        <button className="btn ghost" onClick={() => ctx.reviewSubmission(s.id, 'rejected', comment)} data-testid={`reject-sub-${s.id}`}>Reject</button>
      </div>
    </div>
  );
}

function HistorySubmissionRow({ s, ctx }) {
  const [comment, setComment] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [photos, setPhotos] = React.useState(s.photos && s.photos.length ? s.photos : (s.photo ? [s.photo] : []));
  React.useEffect(() => {
    let cancelled = false;
    if ((s.photoCount || 0) > photos.length) {
      ctx.fetchSubmissionPhotos(s.id).then((data) => { if (!cancelled && data?.photos) setPhotos(data.photos); });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.id]);
  return (
    <div className="item" data-testid={`history-submission-${s.id}`} style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <strong>{s.userName}</strong>
        <div className="meta"><span>{s.challengeTitle}</span> · <span className={`pill ${s.status === 'approved' ? 'green' : s.status === 'needs_changes' ? 'yellow' : ''}`}>{s.status === 'approved' ? 'Approved' : s.status === 'needs_changes' ? 'Sent back for changes' : 'Rejected'}</span></div>
        {s.note && <p style={{ marginTop: 6, fontSize: 14 }}>{s.note}</p>}
        {photos.map((p, i) => (
          <img key={i} src={p} alt="entry" style={{ marginTop: 8, marginRight: 6, maxWidth: 220, borderRadius: 12, border: '1px solid var(--line-strong)' }} />
        ))}
        {s.adminComment && <div className="meta" style={{ marginTop: 6 }}>Coach note: {s.adminComment}</div>}
        {open && (
          <input placeholder="Note to the member about why this was reopened" value={comment} onChange={e => setComment(e.target.value)} data-testid={`reopen-comment-${s.id}`} style={{ marginTop: 8, width: '100%' }} />
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {!open ? (
          <button className="btn ghost" onClick={() => setOpen(true)} data-testid={`reopen-sub-${s.id}`}>Remove / reopen</button>
        ) : (
          <button className="btn" onClick={() => ctx.reviewSubmission(s.id, 'pending', comment)} data-testid={`confirm-reopen-sub-${s.id}`}>
            {s.status === 'approved' ? 'Undo approval & send back' : 'Send back to review'}
          </button>
        )}
      </div>
    </div>
  );
}

function EditChallengeForm({ c, ctx, onDone }) {
  const [photo, setPhoto] = React.useState(null);
  const submit = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const ok = await ctx.editChallenge(c.id, {
      title: f.get('title'),
      description: f.get('description'),
      target: Number(f.get('target') || 1),
      unit: f.get('unit'),
      period: f.get('period'),
      pointsAward: Number(f.get('pointsAward') || 0),
      photo,
    });
    if (ok) onDone();
  };
  return (
    <form className="form" onSubmit={submit} data-testid={`edit-challenge-form-${c.id}`} style={{ width: '100%' }}>
      <div className="notice">Editing keeps this challenge live — everyone's progress so far stays exactly as it is.</div>
      <Field label="Title"><input name="title" required defaultValue={c.title} data-testid={`edit-challenge-title-${c.id}`} /></Field>
      <Field label="Description"><textarea name="description" defaultValue={c.description || ''} data-testid={`edit-challenge-desc-${c.id}`} /></Field>
      {c.hasPhoto && !photo && (
        <div className="sess-photo-preview" style={{ marginBottom: 4 }} data-testid={`edit-challenge-current-photo-${c.id}`}>
          <img src={ctx.challengePhotoUrl(c.id, c.photoV)} alt={c.title} />
          <div className="meta" style={{ marginTop: 4 }}>Current photo on file — upload a new one below to replace it.</div>
        </div>
      )}
      <CoverPhotoPicker photo={photo} setPhoto={setPhoto} toast={ctx.toast} resizeMax={1200} label="Cover photo (optional)" testidPrefix={`edit-challenge-cover-${c.id}`} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Field label="Target"><input name="target" type="number" min="1" required defaultValue={c.target} data-testid={`edit-challenge-target-${c.id}`} /></Field>
        <Field label="Unit"><input name="unit" required defaultValue={c.unit} data-testid={`edit-challenge-unit-${c.id}`} /></Field>
        <Field label="Time frame">
          <select name="period" defaultValue={c.period} data-testid={`edit-challenge-period-${c.id}`}>
            <option value="day">Daily</option>
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
            <option value="one-off">One-off</option>
          </select>
        </Field>
      </div>
      <Field label="Points on approval"><input name="pointsAward" type="number" min="0" defaultValue={c.pointsAward} data-testid={`edit-challenge-points-${c.id}`} /></Field>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn" data-testid={`edit-challenge-save-${c.id}`}>Save changes</button>
        <button type="button" className="btn secondary" onClick={onDone} data-testid={`edit-challenge-cancel-${c.id}`}>Cancel</button>
      </div>
    </form>
  );
}

function EndChallengeControls({ c, ctx, state }) {
  const [open, setOpen] = React.useState(false);
  const [winnerId, setWinnerId] = React.useState('');
  const [bonus, setBonus] = React.useState(100);
  // Build the shortlist: anyone who has an approved submission for this challenge
  const submissions = state.pendingChallengeSubmissions || [];
  // Fallback — approved submitters aren't in bootstrap, so allow ALL non-admin members as fallback
  const candidates = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    submissions.filter(s => s.challengeId === c.id).forEach(s => {
      if (!seen.has(s.userId)) { seen.add(s.userId); out.push({ id: s.userId, name: s.userName }); }
    });
    state.members.filter(m => m.role !== 'Admin' && String(m.status || '').toLowerCase() === 'active').forEach(m => {
      if (!seen.has(m.id)) { seen.add(m.id); out.push({ id: m.id, name: m.name, avatar: m.avatar }); }
    });
    return out;
  }, [c.id, submissions, state.members]);

  const doEnd = async () => {
    const payload = winnerId ? { winnerId, bonus: Number(bonus) || 0 } : {};
    const winner = candidates.find(m => m.id === winnerId);
    const label = winnerId
      ? `Complete this challenge and award ${winner?.name} +${Number(bonus) || 0} points?`
      : 'Complete this challenge? Everyone who took part still keeps their submission points.';
    if (!window.confirm(label)) return;
    const ok = await ctx.endChallenge(c.id, payload);
    if (ok) setOpen(false);
  };

  if (!open) {
    return (
      <button className="btn secondary" onClick={() => setOpen(true)} data-testid={`end-challenge-${c.id}`}>
        Complete challenge…
      </button>
    );
  }
  return (
    <div className="end-challenge-panel" data-testid={`end-challenge-panel-${c.id}`}>
      <div className="eyebrow" style={{ marginBottom: 6, color: 'var(--spc-yellow)' }}>Was there a winner?</div>
      <label className="end-ch-row">
        <input type="radio" name={`w-${c.id}`} checked={winnerId === ''} onChange={() => setWinnerId('')} />
        <span><strong>Just complete the challenge</strong><small>No overall winner — everyone who took part still keeps the points they earned along the way.</small></span>
      </label>
      <div className="end-ch-list">
        {candidates.length === 0 && <div className="empty" style={{ padding: 12 }}>No members to pick from.</div>}
        {candidates.map(m => (
          <label className="end-ch-row" key={m.id} data-testid={`winner-option-${m.id}`}>
            <input type="radio" name={`w-${c.id}`} checked={winnerId === m.id} onChange={() => setWinnerId(m.id)} />
            <span>
              <strong>{m.name}</strong>
              <small>{submissions.filter(s => s.challengeId === c.id && s.userId === m.id).length ? 'Submitted proof' : 'Member'}</small>
            </span>
          </label>
        ))}
      </div>
      {winnerId && (
        <div className="end-ch-bonus">
          <label>Winner bonus</label>
          <input type="number" value={bonus} onChange={(e) => setBonus(e.target.value)} min="0" data-testid="end-ch-bonus" />
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <button className="btn" onClick={doEnd} data-testid={`end-ch-confirm-${c.id}`}>
          {winnerId ? 'Complete & award winner' : 'Complete challenge'}
        </button>
        <button className="btn ghost" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

const BROADCAST_TYPE_LABEL = {
  announcement: 'Announcement',
  breaking_news: 'Breaking News',
  challenge: 'Challenge',
  session: 'New session',
};

function AdminAuditLog({ ctx }) {
  const [rows, setRows] = React.useState(null);
  const [undoingId, setUndoingId] = React.useState(null);

  const load = React.useCallback(async () => {
    try { const { data } = await ctx.getAuditLog(); setRows(data.log); } catch (e) { setRows([]); }
  }, [ctx]);
  React.useEffect(() => { load(); }, [load]);

  const undo = async (r) => {
    if (!window.confirm(`Undo "${r.action}"? This restores everything it removed or changed.`)) return;
    setUndoingId(r.id);
    await ctx.undoAuditAction(r.id);
    setUndoingId(null);
    load();
  };

  if (rows === null) return <div className="empty">Loading recent actions…</div>;
  return (
    <div>
      <div className="notice">The last 100 destructive admin actions — deleted sessions, cancelled series, removed members, discount codes, challenges and the danger-zone resets. Made a mistake? Undo it here, as long as it hasn't already been undone.</div>
      {!rows.length ? (
        <div className="empty" data-testid="admin-audit-log-empty">No admin actions recorded yet.</div>
      ) : (
        <div className="list">
          {rows.map(r => (
            <div className="item" key={r.id} style={{ alignItems: 'flex-start' }} data-testid={`audit-log-${r.id}`}>
              <div>
                <strong style={{ display: 'block' }}>{r.action}</strong>
                <div className="meta">{r.actorName} · {new Date(r.createdAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
              </div>
              {r.undone ? (
                r.undoWarnings?.length ? (
                  <div style={{ textAlign: 'right' }}>
                    <span className="pill orange" data-testid={`audit-log-partial-${r.id}`}>Partly undone</span>
                    <div className="meta" style={{ maxWidth: 220, marginTop: 4 }} data-testid={`audit-log-warnings-${r.id}`}>{r.undoWarnings.join(' ')}</div>
                  </div>
                ) : (
                  <span className="pill green" data-testid={`audit-log-undone-${r.id}`}>Undone</span>
                )
              ) : r.undoable ? (
                <button className="btn secondary" disabled={undoingId === r.id} onClick={() => undo(r)} data-testid={`undo-audit-${r.id}`}>
                  {undoingId === r.id ? 'Undoing…' : 'Undo'}
                </button>
              ) : (
                <span className="pill" data-testid={`audit-log-notundoable-${r.id}`}>Not undoable</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminBroadcasts({ ctx }) {
  const [rows, setRows] = React.useState(null);
  const [removingId, setRemovingId] = React.useState(null);

  const load = React.useCallback(async () => {
    try { const { data } = await ctx.getBroadcasts(); setRows(data.broadcasts); } catch (e) { setRows([]); }
  }, [ctx]);
  React.useEffect(() => { load(); }, [load]);

  const remove = async (b) => {
    if (!window.confirm(`Remove "${b.title}" from every member's notification bell? This can't be undone.`)) return;
    setRemovingId(b.id);
    const ok = await ctx.deleteBroadcast(b.id);
    setRemovingId(null);
    if (ok) setRows(r => r.filter(x => x.id !== b.id));
  };

  if (rows === null) return <div className="empty">Loading broadcasts…</div>;
  return (
    <div>
      <div className="notice">The last 20 broadcasts sent to every member's notification bell (Announcements, Breaking News, new Challenges and new Sessions). Sent one by mistake? Remove it here — it disappears from everyone's bell immediately. Note: a push notification already delivered to a phone can't be recalled, only the in-app bell entry.</div>
      {!rows.length ? (
        <div className="empty" data-testid="admin-broadcasts-empty">No broadcasts sent yet. Post an Announcement, Breaking News, Challenge or Session and it'll appear here, ready to remove if needed.</div>
      ) : (
        <div className="list">
          {rows.map(b => (
            <div className="item" key={b.id} style={{ alignItems: 'flex-start' }} data-testid={`broadcast-${b.id}`}>
              <div>
                <span className="pill">{BROADCAST_TYPE_LABEL[b.type] || b.type}</span>
                <strong style={{ display: 'block', marginTop: 7 }}>{b.title}</strong>
                <p style={{ margin: '4px 0 0' }}>{b.body}</p>
                <div className="meta">{b.recipientCount} recipients · {new Date(b.createdAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
              </div>
              <button className="btn danger" disabled={removingId === b.id} onClick={() => remove(b)} data-testid={`remove-broadcast-${b.id}`}>
                {removingId === b.id ? 'Removing…' : 'Remove'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminBreakingNews({ state, ctx }) {
  const current = state.breakingNews;
  const [title, setTitle] = React.useState('');
  const [body, setBody] = React.useState('');
  const [file, setFile] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const fileInputRef = React.useRef(null);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    const isVideo = f.type.startsWith('video/');
    const maxMB = isVideo ? 50 : 10;
    if (f.size > maxMB * 1024 * 1024) {
      ctx.toast(`File too large — max ${maxMB}MB for a ${isVideo ? 'video' : 'photo'}`);
      e.target.value = ''; setFile(null); return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) { ctx.toast('Add a title and a message'); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append('title', title.trim());
    fd.append('body', body.trim());
    if (file) fd.append('file', file);
    const ok = await ctx.setBreakingNews(fd);
    setBusy(false);
    if (ok) { setTitle(''); setBody(''); setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };
  const clear = async () => {
    if (!window.confirm('Clear the current breaking news? It will stop showing to members.')) return;
    await ctx.clearBreakingNews();
  };

  return (
    <div>
      <div className="notice"><strong>Big announcements only.</strong> Whatever you write here pops up for every member the next time they log in (or within ~15 seconds if they're already online), plus a push notification and an entry in their notification bell. Posting a new one replaces the current one and shows again to everyone.</div>
      {current ? (
        <div className="item" style={{ alignItems: 'flex-start', marginBottom: 16 }} data-testid="current-breaking-news">
          <div>
            <span className="pill yellow">Currently live</span>
            <strong style={{ display: 'block', marginTop: 7 }}>{current.title}</strong>
            <p style={{ margin: '4px 0 0' }}>{current.body}</p>
            {current.mediaType === 'photo' && (
              <img src={ctx.breakingNewsMediaUrl()} alt="" style={{ maxWidth: 200, borderRadius: 10, marginTop: 8, display: 'block' }} data-testid="current-breaking-news-photo" />
            )}
            {current.mediaType === 'video' && (
              <video src={ctx.breakingNewsMediaUrl()} controls style={{ maxWidth: 240, borderRadius: 10, marginTop: 8, display: 'block' }} data-testid="current-breaking-news-video" />
            )}
          </div>
          <button className="btn danger" onClick={clear} data-testid="clear-breaking-news-btn">Clear</button>
        </div>
      ) : (
        <div className="empty" style={{ marginBottom: 16 }} data-testid="no-breaking-news">Nothing live right now.</div>
      )}
      <form className="form" onSubmit={submit} data-testid="breaking-news-form">
        <Field label="Title"><input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. New coach joining SPC!" data-testid="breaking-news-title-input" /></Field>
        <Field label="Message"><textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="What's the big news?" data-testid="breaking-news-body-input" /></Field>
        <Field label="Photo or video (optional)">
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm" onChange={onFileChange} data-testid="breaking-news-file-input" />
        </Field>
        {file && <div className="meta" data-testid="breaking-news-file-selected">{file.name} · {(file.size / (1024 * 1024)).toFixed(1)}MB</div>}
        <button className="btn" disabled={busy} data-testid="post-breaking-news-btn"><Megaphone size={15} /> {busy ? 'Posting…' : 'Post breaking news'}</button>
      </form>
    </div>
  );
}

function AdminChallenge({ state, ctx, initialTab }) {
  const [tab, setTab] = React.useState(initialTab || 'new'); // new | library | active | review | ended
  const [library, setLibrary] = React.useState([]);
  const [tierFilter, setTierFilter] = React.useState('all');
  const [prefill, setPrefill] = React.useState(null);
  const [editingId, setEditingId] = React.useState(null);
  const list = state.challenges || [];
  const active = list.filter(c => c.status === 'active');
  const ended = list.filter(c => c.status !== 'active');
  const pending = state.pendingChallengeSubmissions || [];
  const reviewed = state.reviewedChallengeSubmissions || [];
  const [photo, setPhoto] = React.useState(null);

  React.useEffect(() => {
    if (tab === 'library' && library.length === 0) {
      ctx.getChallengeLibrary().then(setLibrary);
    }
  }, [tab, library.length, ctx]);

  const applyTemplate = (t) => { setPrefill(t); setTab('new'); };
  const toggleHidden = async (t) => {
    await ctx.toggleLibraryItem(t.title);
    setLibrary(await ctx.getChallengeLibrary());
  };

  const submit = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const ok = await ctx.updateChallenge({
      title: f.get('title'),
      description: f.get('description'),
      target: Number(f.get('target') || 1),
      unit: f.get('unit'),
      period: f.get('period'),
      pointsAward: Number(f.get('pointsAward') || 0),
      photo,
    });
    if (ok) { setPhoto(null); setTab('active'); }
  };

  return (
    <div>
      <div className="arcade-tabs" role="tablist" style={{ marginBottom: 12 }}>
        <button className={`arcade-tab ${tab === 'new' ? 'active' : ''}`} onClick={() => setTab('new')} data-testid="admin-ch-tab-new">New</button>
        <button className={`arcade-tab ${tab === 'library' ? 'active' : ''}`} onClick={() => setTab('library')} data-testid="admin-ch-tab-library">Library</button>
        <button className={`arcade-tab ${tab === 'active' ? 'active' : ''}`} onClick={() => setTab('active')} data-testid="admin-ch-tab-active">Active ({active.length})</button>
        <button className={`arcade-tab ${tab === 'review' ? 'active' : ''}`} onClick={() => setTab('review')} data-testid="admin-ch-tab-review">Review {pending.length > 0 && <span className="pill yellow" style={{ marginLeft: 6 }}>{pending.length}</span>}</button>
        <button className={`arcade-tab ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')} data-testid="admin-ch-tab-history">History</button>
        <button className={`arcade-tab ${tab === 'ended' ? 'active' : ''}`} onClick={() => setTab('ended')} data-testid="admin-ch-tab-ended">Past ({ended.length})</button>
      </div>

      {tab === 'library' && (
        <ChallengeLibraryPanel library={library} tierFilter={tierFilter} setTierFilter={setTierFilter} applyTemplate={applyTemplate} toggleHidden={toggleHidden} />
      )}

      {tab === 'new' && (
        <form className="form" onSubmit={submit} data-testid="admin-challenge-form">
          <div className="notice"><strong>Any challenge, any theme.</strong> "Sunset run", "Silly o'clock 5k", "Bake for the club" — write it, pick how long it runs, and members will send in their entries with a photo. Or head to the <strong>Library</strong> tab for 27 ready-made templates.</div>
          <Field label="Title"><input name="title" required placeholder="e.g. Sunset Run September" defaultValue={prefill?.title || ''} key={prefill?.title || 'blank'} data-testid="challenge-title" /></Field>
          <Field label="Description"><textarea name="description" placeholder="What do members need to do? Where? Any rules?" defaultValue={prefill?.description || ''} key={`desc-${prefill?.title || 'blank'}`} data-testid="challenge-desc" /></Field>
          <CoverPhotoPicker photo={photo} setPhoto={setPhoto} toast={ctx.toast} resizeMax={1200} label="Cover photo (optional)" testidPrefix="challenge-cover-photo" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }} key={`row-${prefill?.title || 'blank'}`}>
            <Field label="Target"><input name="target" type="number" min="1" required defaultValue={prefill?.target || 1} data-testid="challenge-target" /></Field>
            <Field label="Unit"><input name="unit" required defaultValue={prefill?.unit || "completions"} placeholder="completions, runs…" data-testid="challenge-unit" /></Field>
            <Field label="Time frame">
              <select name="period" defaultValue={prefill?.period || "month"} data-testid="challenge-period">
                <option value="day">Daily</option>
                <option value="week">Weekly</option>
                <option value="month">Monthly</option>
                <option value="one-off">One-off</option>
              </select>
            </Field>
          </div>
          <Field label="Points on approval"><input name="pointsAward" type="number" min="0" defaultValue={prefill?.pointsAward || 50} key={`pts-${prefill?.title || 'blank'}`} data-testid="challenge-points" /></Field>
          <div className="notice">This runs <strong>alongside</strong> any other active challenges — nothing else gets closed, and everyone's progress on other challenges is untouched.</div>
          <button className="btn" data-testid="challenge-save-btn"><Trophy size={15} /> Launch challenge</button>
        </form>
      )}

      {tab === 'active' && (
        active.length ? <div className="list">{active.map(c => (
          <div className="item" key={c.id} data-testid={`admin-active-${c.id}`} style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {editingId === c.id ? (
              <EditChallengeForm c={c} ctx={ctx} onDone={() => setEditingId(null)} />
            ) : (
              <>
                <div style={{ flex: 1 }}><strong>{c.title}</strong><div className="meta">{c.target} {c.unit} · {c.period} · {c.pointsAward} pts</div></div>
                <button type="button" className="btn secondary" onClick={() => setEditingId(c.id)} data-testid={`admin-edit-challenge-${c.id}`}>Edit</button>
                <EndChallengeControls c={c} ctx={ctx} state={state} />
              </>
            )}
          </div>
        ))}</div> : <div className="empty">No active challenge yet — launch one in the New tab.</div>
      )}

      {tab === 'review' && (
        pending.length ? <div className="list">{pending.map(s => <ReviewSubmissionRow key={s.id} s={s} ctx={ctx} />)}</div> : <div className="empty">Nothing to review right now.</div>
      )}

      {tab === 'history' && (
        reviewed.length ? <div className="list" data-testid="challenge-history-list">{reviewed.map(s => <HistorySubmissionRow key={s.id} s={s} ctx={ctx} />)}</div> : <div className="empty" data-testid="challenge-history-empty">No reviewed entries yet.</div>
      )}

      {tab === 'ended' && (
        ended.length ? <div className="list">{ended.map(c => (
          <div className="item" key={c.id}>
            <div><strong>{c.title}</strong><div className="meta">{c.winnerName ? <>Winner: <strong>{c.winnerName}</strong></> : 'No winner declared'}</div></div>
            <span className="pill">{c.target} {c.unit}</span>
          </div>
        ))}</div> : <div className="empty">No past challenges yet.</div>
      )}
    </div>
  );
}

function AdminSettings({ state, ctx }) {
  const s = state.settings || {};
  const [wa, setWa] = React.useState(s.whatsappUrl || '');
  const [tagline, setTagline] = React.useState(s.clubTagline || '');
  const [pts, setPts] = React.useState(s.attendancePoints ?? 25);
  const [windowWeeks, setWindowWeeks] = React.useState(s.bookingWindowWeeks ?? 8);
  const [pointRulesNote, setPointRulesNote] = React.useState(s.pointRulesNote || '');
  const [saving, setSaving] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await ctx.updateSettings({
      attendancePoints: Number(pts),
      whatsappUrl: wa.trim(),
      clubTagline: tagline.trim(),
      bookingWindowWeeks: Number(windowWeeks),
      pointRulesNote: pointRulesNote.trim(),
    });
    setSaving(false);
  };
  return (
    <form className="form" onSubmit={submit} data-testid="admin-settings-form">
      <div className="notice"><strong>These settings shape the new-member experience.</strong> The WhatsApp link appears on the welcome tour so new members can jump straight into the group chat.</div>
      <Field label="WhatsApp group link"><input value={wa} onChange={(e) => setWa(e.target.value)} placeholder="https://chat.whatsapp.com/…" data-testid="settings-whatsapp-input" /></Field>
      <Field label="Club tagline (optional, shown on the welcome tour)"><input value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder="e.g. Smart training. Faster times. Real people." data-testid="settings-tagline-input" /></Field>
      <Field label="Default SPC Points per check-in"><input value={pts} onChange={(e) => setPts(e.target.value)} type="number" min="0" data-testid="settings-points-input" /></Field>
      <Field label="Booking window (weeks members can see ahead)"><input value={windowWeeks} onChange={(e) => setWindowWeeks(e.target.value)} type="number" min="1" data-testid="settings-booking-window-input" /></Field>
      <div className="notice" style={{ marginTop: -4 }}>Members only see sessions within this many weeks from today (Socials always show regardless). You still see every session in this Manage list — each card shows when it'll become visible to members.</div>
      <Field label="Note shown above 'How points are earned' on Benefits (optional)"><textarea value={pointRulesNote} onChange={(e) => setPointRulesNote(e.target.value)} placeholder="e.g. Points are awarded once per eligible activity and may take a short time to appear while submissions are verified." data-testid="settings-point-rules-note-input" /></Field>
      <button className="btn" disabled={saving} data-testid="settings-save-btn">{saving ? 'Saving…' : 'Save settings'}</button>

      <div className="notice" style={{ marginTop: 10 }}>
        <strong>Preview the new-member experience.</strong> Tap below to have the welcome tour play again the next time you log in.
        <button type="button" className="btn secondary" style={{ marginTop: 10 }} onClick={ctx.replayWelcome} data-testid="replay-welcome-btn">Show welcome tour on my next login</button>
      </div>
    </form>
  );
}


function EmailTemplateCard({ t, ctx, onChanged }) {
  const [subject, setSubject] = React.useState(t.subject);
  const [heading, setHeading] = React.useState(t.heading);
  const [body, setBody] = React.useState(t.body);
  const [saving, setSaving] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [toggling, setToggling] = React.useState(false);
  const dirty = subject !== t.subject || heading !== t.heading || body !== t.body;
  const save = async () => { setSaving(true); const ok = await ctx.updateEmailTemplate(t.key, { subject, heading, body }); setSaving(false); if (ok) onChanged(); };
  const reset = async () => { const ok = await ctx.resetEmailTemplate(t.key); if (ok) onChanged(); };
  const sendTest = async () => { setSending(true); await ctx.sendTestEmailTemplate(t.key); setSending(false); };
  const toggle = async () => { setToggling(true); const ok = await ctx.toggleEmailTemplate(t.key, !t.enabled); setToggling(false); if (ok) onChanged(); };
  const locked = t.key === 'password_reset';
  return (
    <div className="card" data-testid={`email-template-${t.key}`} style={!t.enabled ? { opacity: 0.55 } : undefined}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div><strong>{t.label}</strong>{t.customized && <span className="pill yellow" style={{ marginLeft: 8 }}>Customised</span>}</div>
        {locked ? (
          <span className="pill green" data-testid={`email-locked-${t.key}`}>Always on</span>
        ) : (
          <button type="button" className={`btn ${t.enabled ? 'secondary' : ''}`} style={{ padding: '5px 12px', fontSize: 12 }} disabled={toggling} onClick={toggle} data-testid={`email-toggle-${t.key}`}>
            {toggling ? '…' : t.enabled ? 'On — turn off' : 'Off — turn on'}
          </button>
        )}
      </div>
      <p style={{ color: 'var(--muted)', fontSize: 13, margin: '6px 0 12px' }}>Placeholders you can use: <code>{t.placeholders}</code></p>
      <Field label="Subject"><input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid={`email-subject-${t.key}`} /></Field>
      <Field label="Heading"><input value={heading} onChange={(e) => setHeading(e.target.value)} data-testid={`email-heading-${t.key}`} /></Field>
      <Field label="Message"><textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} data-testid={`email-body-${t.key}`} /></Field>
      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <button type="button" className="btn" style={!dirty ? { opacity: 0.4, filter: 'grayscale(.6)' } : undefined} disabled={!dirty || saving} onClick={save} data-testid={`email-save-${t.key}`}>{saving ? 'Saving…' : 'Save'}</button>
        <button type="button" className="btn secondary" disabled={sending} onClick={sendTest} data-testid={`email-test-${t.key}`}>{sending ? 'Sending…' : 'Send test to me'}</button>
        <button type="button" className="btn ghost" disabled={!t.customized} onClick={reset} data-testid={`email-reset-${t.key}`}>Reset to default</button>
      </div>
    </div>
  );
}

function AdminEmailTemplates({ ctx }) {
  const [templates, setTemplates] = React.useState(null);
  const [versions, setVersions] = React.useState({});
  const load = React.useCallback(async () => {
    try { const { data } = await ctx.getEmailTemplates(); setTemplates(data.templates); } catch (e) { setTemplates([]); }
  }, [ctx]);
  React.useEffect(() => { load(); }, [load]);
  const onChanged = async (key) => { await load(); setVersions(v => ({ ...v, [key]: (v[key] || 0) + 1 })); };
  if (templates === null) return <div className="empty">Loading templates…</div>;
  return (
    <>
      <div className="notice">Edit the wording members see in each automated email, or turn any of them off entirely (except password reset, which always stays on). The logo, colours and layout are fixed to match the app — you're just editing the text.</div>
      <div className="list" style={{ marginTop: 12, gap: 14 }}>
        {templates.map(t => <EmailTemplateCard key={`${t.key}-${versions[t.key] || 0}`} t={t} ctx={ctx} onChanged={() => onChanged(t.key)} />)}
      </div>
    </>
  );
}

function AdminAchievements({ state, ctx }) {
  const [expanded, setExpanded] = React.useState(null); // achievement id
  const [pickMember, setPickMember] = React.useState('');
  const [filter, setFilter] = React.useState('all');

  const members = (state.members || []).filter(m => m.role !== 'Admin');
  const categories = Array.from(new Set(state.achievementCatalogue.map(a => a.category)));
  const filtered = state.achievementCatalogue.filter(a => filter === 'all' || a.category === filter);

  const whoHas = (aid) => members.filter(m => (m.unlockedAchievements || []).some(u => u.id === aid));

  return (
    <div>
      <div className="notice">
        <strong>Auto + manual awards.</strong> Most achievements unlock automatically. For hidden or coach-only ones (Volunteer, Pacer, Founder's Pick etc), open a badge below and hand it out — the member gets a push and the confetti fires on their next visit.
      </div>

      <div className="arcade-picker" style={{ marginTop: 12 }}>
        <button className={`arcade-pick ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')} data-testid="ach-filter-all">All ({state.achievementCatalogue.length})</button>
        {categories.map(c => (
          <button key={c} className={`arcade-pick ${filter === c ? 'active' : ''}`} onClick={() => setFilter(c)} data-testid={`ach-filter-${c}`}>{c} ({state.achievementCatalogue.filter(a => a.category === c).length})</button>
        ))}
      </div>

      <div className="list" style={{ marginTop: 12 }}>
        {filtered.map(a => {
          const holders = whoHas(a.id);
          const isOpen = expanded === a.id;
          return (
            <div className="item" key={a.id} data-testid={`ach-row-${a.id}`} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between', width: '100%' }}>
                <div style={{ flex: 1 }}>
                  <span className="pill yellow">{a.category}</span>
                  <strong style={{ display: 'block', marginTop: 6 }}>{a.icon} {a.title}</strong>
                  <div className="meta">{a.description}{a.points ? ' · +' + a.points + ' pts' : ''} · {holders.length} awarded</div>
                </div>
                <button className="btn secondary" onClick={() => { setExpanded(isOpen ? null : a.id); setPickMember(''); }} data-testid={`ach-manage-${a.id}`}>
                  {isOpen ? 'Close' : 'Manage'}
                </button>
              </div>
              {isOpen && (
                <div style={{ marginTop: 10, padding: 12, background: 'rgba(255,255,255,.03)', border: '1px solid var(--line-strong)', borderRadius: 12 }}>
                  <div style={{ marginBottom: 10, fontSize: 13, fontWeight: 700, color: '#c4c8cd' }}>Award to a member</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <select value={pickMember} onChange={(e) => setPickMember(e.target.value)} style={{ flex: 1, minWidth: 200 }} data-testid={`ach-pick-${a.id}`}>
                      <option value="">Choose a member…</option>
                      {members
                        .filter(m => !(m.unlockedAchievements || []).some(u => u.id === a.id))
                        .map(m => <option key={m.id} value={m.id}>{m.name} · {m.role}</option>)}
                    </select>
                    <button className="btn" disabled={!pickMember} onClick={async () => { const ok = await ctx.awardAchievement(pickMember, a.id); if (ok) setPickMember(''); }} data-testid={`ach-award-${a.id}`}>Award</button>
                  </div>

                  {holders.length > 0 && (
                    <>
                      <div style={{ marginTop: 14, marginBottom: 6, fontSize: 13, fontWeight: 700, color: '#c4c8cd' }}>Already awarded to</div>
                      <div className="list" style={{ gap: 6 }}>
                        {holders.map(m => {
                          const rec = (m.unlockedAchievements || []).find(u => u.id === a.id);
                          return (
                            <div className="item" key={m.id} style={{ padding: '8px 12px' }}>
                              <div>
                                <strong>{m.name}</strong>
                                <div className="meta">{m.role} · earned {rec?.earnedAt || ''}</div>
                              </div>
                              <button className="btn ghost" onClick={() => { if (window.confirm(`Remove ${a.title} from ${m.name}?`)) ctx.revokeAchievement(m.id, a.id); }} data-testid={`ach-revoke-${m.id}-${a.id}`}>Remove</button>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function SafetyCardModal({ ctx, state, payload }) {
  const sc = state.member?.safetyCard || {};
  const [busy, setBusy] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const body = {
      iceName: (f.get('iceName') || '').trim(),
      iceNumber: (f.get('iceNumber') || '').trim(),
      allergiesConditions: (f.get('allergiesConditions') || '').trim(),
    };
    if (!body.iceName || !body.iceNumber) {
      ctx.toast('Please fill in ICE name and ICE number');
      return;
    }
    setBusy(true);
    const ok = await ctx.saveSafetyCard(body);
    setBusy(false);
    if (ok) {
      ctx.closeModal();
      if (payload?.onComplete) payload.onComplete();
    }
  };
  return (
    <div data-testid="safety-card-modal">
      <div className="notice soft" data-testid="safety-card-intro">Kept on file so a coach or session leader can act fast if there's ever an emergency during an SPC session. Required before you can book.</div>
      <form className="form" onSubmit={submit} data-testid="safety-card-form">
        <Field label="ICE name (In Case of Emergency contact)"><input name="iceName" defaultValue={sc.iceName || ''} required data-testid="safety-card-ice-name" /></Field>
        <Field label="ICE number"><input name="iceNumber" defaultValue={sc.iceNumber || ''} required data-testid="safety-card-ice-number" /></Field>
        <Field label="Allergies / medical conditions (optional)"><textarea name="allergiesConditions" defaultValue={sc.allergiesConditions || ''} placeholder="e.g. none, or asthma / nut allergy…" data-testid="safety-card-allergies" /></Field>
        <button className="btn" disabled={busy} data-testid="safety-card-save-btn">{busy ? 'Saving…' : 'Save Safety Card'}</button>
      </form>
    </div>
  );
}

export function SessionSafetyCards({ ctx, s }) {
  const [open, setOpen] = React.useState(false);
  const [cards, setCards] = React.useState(null);
  const toggle = async () => {
    if (cards === null) {
      try { const { data } = await ctx.getSessionSafetyCards(s.id); setCards(data.cards || []); }
      catch (e) { ctx.toast(errMsg(e)); return; }
    }
    setOpen(o => !o);
  };
  const memberCount = (s.booked || 0) - (s.guestAttendees?.length || 0);
  if (!memberCount) return null;
  return (
    <div style={{ marginBottom: 4 }}>
      <button type="button" className="btn secondary" style={{ width: '100%' }} onClick={toggle} data-testid={`view-safety-cards-btn-${s.id}`}>
        {open ? 'Hide safety info' : `🆘 View safety info (${memberCount} booked)`}
      </button>
      {open && (
        <div className="list" style={{ marginTop: 8 }} data-testid="session-safety-cards-list">
          {cards === null ? <div className="empty">Loading…</div> : cards.length === 0 ? <div className="empty">No booked members.</div> : cards.map(c => (
            <div className="item" key={c.id} data-testid={`safety-card-row-${c.id}`}>
              <div className="item-main">
                <div className="item-title">{c.name}{!c.complete && <span className="pill soft" style={{ marginLeft: 6 }}>Not provided</span>}</div>
                {c.complete && <div className="meta"><span>ICE: {c.iceName} · {c.iceNumber}</span></div>}
                {c.complete && c.allergiesConditions && <div className="meta"><span>Allergies/conditions: {c.allergiesConditions}</span></div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TransferMemberPanel({ ctx, m, bookedIds, onCancel, onConfirm }) {
  const [q, setQ] = React.useState('');
  const [mode, setMode] = React.useState('all');
  const [busy, setBusy] = React.useState(false);
  const results = q.trim().length < 2 ? [] : ctx.state.members
    .filter(mem => mem.role !== 'Admin' && !bookedIds.has(mem.id) && mem.id !== m.id && (mem.name || '').toLowerCase().includes(q.trim().toLowerCase()))
    .slice(0, 6);
  const confirm = async (target) => {
    if (!window.confirm(`Transfer ${m.ticketCount > 1 && mode === 'all' ? `all ${m.ticketCount} tickets` : 'this booking'} from ${m.name} to ${target.name}?`)) return;
    setBusy(true);
    const ok = await onConfirm(target, mode);
    setBusy(false);
    if (ok) onCancel();
  };
  return (
    <div className="notice soft" data-testid={`transfer-panel-${m.id}`} style={{ margin: '6px 0 10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontWeight: 700 }}>Transfer {m.name}'s booking to…</div>
        <button type="button" className="btn ghost" style={{ padding: '4px 8px' }} onClick={onCancel} data-testid={`transfer-cancel-${m.id}`}>Cancel</button>
      </div>
      {m.ticketCount > 1 && (
        <div className="seg" style={{ marginBottom: 10 }} data-testid={`transfer-mode-${m.id}`}>
          <button type="button" className={mode === 'all' ? 'active' : ''} onClick={() => setMode('all')} data-testid={`transfer-mode-all-${m.id}`}>All {m.ticketCount} tickets</button>
          <button type="button" className={mode === 'one' ? 'active' : ''} onClick={() => setMode('one')} data-testid={`transfer-mode-one-${m.id}`}>1 ticket only</button>
        </div>
      )}
      <input
        value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search member by name…"
        disabled={busy} data-testid={`transfer-search-input-${m.id}`}
      />
      {results.length > 0 && (
        <div className="list" style={{ marginTop: 8 }}>
          {results.map(mem => (
            <div className="item" key={mem.id} style={{ cursor: 'pointer', padding: '8px 10px' }} onClick={() => !busy && confirm(mem)} data-testid={`transfer-result-${mem.id}`}>
              <span>{mem.name}</span>
              <button type="button" className="btn secondary" style={{ padding: '4px 10px' }} disabled={busy}>Transfer</button>
            </div>
          ))}
        </div>
      )}
      {q.trim().length >= 2 && results.length === 0 && <div className="meta" style={{ marginTop: 6 }}>No matching members found (or already booked).</div>}
    </div>
  );
}

export function BookedMembersList({ s, onRemove, onTransfer, onRemoveGuest }) {
  const ctx = useSpc();
  const [roster, setRoster] = React.useState(null);
  const [transferFor, setTransferFor] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    if ((s.bookedMembers?.length || s.attendedMembers?.length)) {
      ctx.fetchSessionRoster(s.id).then((data) => { if (!cancelled && data) setRoster(data); });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.id]);
  const bookedMembers = (roster?.bookedMembers) || s.bookedMembers;
  const sittingOutMembers = roster?.sittingOutMembers;
  const showSittingOut = ctx.role !== 'member' && s.seriesId && sittingOutMembers && sittingOutMembers.length > 0;
  const guestAttendees = roster?.guestAttendees || s.guestAttendees || [];
  const paceEnabled = roster?.paceGroups || s.paceGroups;
  const paceDefs = roster?.paceGroupDefs || ctx.state.paceGroupDefs || [];
  if ((!bookedMembers || bookedMembers.length === 0) && !showSittingOut && guestAttendees.length === 0) return null;
  const bookedIds = new Set((bookedMembers || []).map(m => m.id));
  const isStaff = ctx.role !== 'member';
  const hasCatering = roster?.hasCatering || s.hasCatering;
  const dietaryNotes = isStaff && hasCatering ? (bookedMembers || []).filter(m => m.dietaryNote) : [];
  const isRunSession = s.type === 'Run Session';
  const leaderMembers = isRunSession ? (bookedMembers || []).filter(m => m.isLeader) : [];
  const regularMembers = isRunSession ? (bookedMembers || []).filter(m => !m.isLeader) : (bookedMembers || []);

  const renderRow = (m, isLeader) => (
    <div key={m.id}>
      <div className="booked-member-row" data-testid={`booked-member-${m.id}`}>
        {m.avatar ? <img src={m.avatar} alt={m.name} /> : <span className="booked-member-initial">{(m.name || 'M')[0].toUpperCase()}</span>}
        <span>
          {m.name || 'Member'}{m.ticketCount > 1 ? ` ×${m.ticketCount}` : ''}
          {isLeader && <span className="leader-tag" data-testid={`leader-tag-${m.id}`}>{(m.roleNames && m.roleNames[0]) || 'Leader'}</span>}
          {m.guestNames && m.guestNames.length > 0 && <span className="meta" style={{ marginLeft: 6 }}>+ {m.guestNames.join(', ')}</span>}
          {isStaff && hasCatering && m.dietaryNote && <span className="meta" style={{ marginLeft: 6 }} data-testid={`inline-dietary-note-${m.id}`}>🍽 {m.dietaryNote}</span>}
        </span>
        {onTransfer && (
          <button type="button" className="booked-member-transfer" title="Transfer to another member" onClick={() => setTransferFor(transferFor === m.id ? null : m.id)} data-testid={`transfer-booked-member-${m.id}`}><ArrowRightLeft size={13} /></button>
        )}
        {onRemove && (
          <button type="button" className="booked-member-remove" title="Remove from session" onClick={() => onRemove(m)} data-testid={`remove-booked-member-${m.id}`}>×</button>
        )}
      </div>
      {transferFor === m.id && (
        <TransferMemberPanel ctx={ctx} m={m} bookedIds={bookedIds} onCancel={() => setTransferFor(null)} onConfirm={(target, mode) => onTransfer(m, target, mode)} />
      )}
    </div>
  );

  return (
    <div className="booked-members" data-testid="booked-members-list">
      {dietaryNotes.length > 0 && (
        <div className="notice soft" data-testid="dietary-summary-panel" style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>🍽 Dietary requirements ({dietaryNotes.length})</div>
          {dietaryNotes.map(m => (
            <div className="meta" key={m.id} data-testid={`dietary-summary-row-${m.id}`}><strong>{m.name}:</strong> {m.dietaryNote}</div>
          ))}
        </div>
      )}
      {leaderMembers.length > 0 && (
        <div className="leaders-section" style={{ marginBottom: 12 }}>
          <div className="booked-members-head" data-testid="meet-your-leaders-head"><ShieldCheck size={14} style={{ marginRight: 5, verticalAlign: -2 }} />Meet your leaders ({leaderMembers.length})</div>
          <div className="booked-members-rows">{leaderMembers.map(m => renderRow(m, true))}</div>
        </div>
      )}
      {regularMembers.length > 0 && (
        paceEnabled && paceDefs.length > 0 ? (
          <>
            {paceDefs.map(g => {
              const group = regularMembers.filter(m => m.paceGroup === g.key);
              if (!group.length) return null;
              return (
                <div key={g.key} style={{ marginBottom: 10 }}>
                  <div className="booked-members-head" data-testid={`pace-group-head-${g.key}`}>{g.emoji} {g.label} /mile · {g.kmLabel} /km ({group.length})</div>
                  <div className="booked-members-rows">{group.map(m => renderRow(m, false))}</div>
                </div>
              );
            })}
            {(() => {
              const noGroup = regularMembers.filter(m => !m.paceGroup);
              if (!noGroup.length) return null;
              return (
                <div>
                  <div className="booked-members-head">Group not chosen ({noGroup.length})</div>
                  <div className="booked-members-rows">{noGroup.map(m => renderRow(m, false))}</div>
                </div>
              );
            })()}
          </>
        ) : (
          <>
            <div className="booked-members-head">Who's booked ({bookedMembers.reduce((sum, m) => sum + (m.ticketCount || 1), 0)})</div>
            <div className="booked-members-rows">{regularMembers.map(m => renderRow(m, false))}</div>
          </>
        )
      )}
      {showSittingOut && (
        <>
          <div className="booked-members-head" data-testid="sitting-out-members-head" style={{ marginTop: bookedMembers?.length ? 14 : 0 }}>Sitting out this week ({sittingOutMembers.length})</div>
          <div className="booked-members-rows">
            {sittingOutMembers.map(m => (
              <div key={m.id} className="booked-member-row sitting-out" data-testid={`sitting-out-member-${m.id}`}>
                {m.avatar ? <img src={m.avatar} alt={m.name} /> : <span className="booked-member-initial">{(m.name || 'M')[0].toUpperCase()}</span>}
                <span>{m.name || 'Member'}</span>
              </div>
            ))}
          </div>
        </>
      )}
      {guestAttendees.length > 0 && (
        <>
          <div className="booked-members-head" data-testid="guests-head" style={{ marginTop: (bookedMembers?.length || showSittingOut) ? 14 : 0 }}>Guests ({guestAttendees.length})</div>
          <div className="booked-members-rows">
            {guestAttendees.map(g => (
              <div key={g.id} className="booked-member-row" data-testid={`guest-row-${g.id}`}>
                <span className="booked-member-initial">{(g.name || 'G')[0].toUpperCase()}</span>
                <span><strong>{g.name}</strong><div className="meta">Guest</div></span>
                {onRemoveGuest && (
                  <button type="button" className="booked-member-remove" title="Remove guest" onClick={() => onRemoveGuest(g)} data-testid={`remove-guest-${g.id}`}>×</button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function BuyTickets({ ctx, s }) {
  const alreadyBooked = !!s.mine;
  const spotsLeft = Math.max(0, (s.capacity || 0) - (s.booked || 0));
  const [qty, setQty] = React.useState(1);
  const [names, setNames] = React.useState([]);
  const [note, setNote] = React.useState(s.myDietaryNote || '');
  const [busy, setBusy] = React.useState(false);
  const neededNames = alreadyBooked ? qty : Math.max(0, qty - 1);

  React.useEffect(() => {
    setNames(prev => {
      const next = prev.slice(0, neededNames);
      while (next.length < neededNames) next.push('');
      return next;
    });
  }, [neededNames]);

  React.useEffect(() => { if (qty > spotsLeft) setQty(Math.max(1, spotsLeft)); }, [spotsLeft, qty]);

  const fee = s.memberPaysFee ? Math.round((s.priceAmount * qty * 0.02 + 0.20) * 100) / 100 : 0;
  const total = Math.round((s.priceAmount * qty + fee) * 100) / 100;
  const canSubmit = !busy && spotsLeft > 0 && qty >= 1 && qty <= spotsLeft && names.every(n => n.trim().length > 0);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try { await ctx.startSessionCheckout(s.id, qty, names.map(n => n.trim()), s.hasCatering ? note.trim() : null); }
    catch (e) { setBusy(false); }
  };

  if (spotsLeft <= 0) {
    return <div className="empty" data-testid="buy-tickets-full">This session is now full.</div>;
  }

  return (
    <div data-testid="buy-tickets-modal">
      <p className="meta" style={{ marginTop: 0 }}>
        {alreadyBooked
          ? `You already have a ticket for "${s.title}". Buy extra tickets for guests below.`
          : `£${s.priceAmount} per ticket · ${spotsLeft} spot${spotsLeft === 1 ? '' : 's'} left.`}
      </p>
      <Field label={alreadyBooked ? 'Extra tickets to buy' : 'Tickets (including yours)'}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button type="button" className="btn ghost" data-testid="buy-tickets-qty-minus" onClick={() => setQty(q => Math.max(1, q - 1))} disabled={qty <= 1}>−</button>
          <strong data-testid="buy-tickets-qty-value" style={{ fontSize: 18, minWidth: 24, textAlign: 'center' }}>{qty}</strong>
          <button type="button" className="btn ghost" data-testid="buy-tickets-qty-plus" onClick={() => setQty(q => Math.min(spotsLeft, q + 1))} disabled={qty >= spotsLeft}>+</button>
        </div>
      </Field>
      {neededNames > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className="meta" style={{ marginBottom: 6 }}>Guest name{neededNames > 1 ? 's' : ''}</div>
          {names.map((n, i) => (
            <input
              key={i}
              type="text"
              placeholder={`Guest ${i + 1} name`}
              value={n}
              maxLength={60}
              style={{ marginBottom: 8 }}
              onChange={(e) => setNames(prev => prev.map((v, idx) => idx === i ? e.target.value : v))}
              data-testid={`buy-tickets-guest-name-${i}`}
            />
          ))}
        </div>
      )}
      {s.hasCatering && (
        <Field label="Dietary requirements / notes (optional)">
          <textarea rows={2} maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. vegetarian, nut allergy, gluten-free…" data-testid="buy-tickets-dietary-note" />
        </Field>
      )}
      <div className="notice soft" style={{ marginTop: 14 }} data-testid="buy-tickets-total">
        <strong>Total: £{total.toFixed(2)}</strong>
        {fee > 0 && <div className="meta">Includes £{fee.toFixed(2)} card processing fee</div>}
      </div>
      <button
        type="button"
        className="btn"
        style={{ width: '100%', marginTop: 14 }}
        disabled={!canSubmit}
        onClick={submit}
        data-testid="buy-tickets-submit"
      >
        {busy ? 'Redirecting…' : `Pay £${total.toFixed(2)} & checkout`}
      </button>
    </div>
  );
}

export function BookWithNote({ ctx, s }) {
  const [paceGroup, setPaceGroup] = React.useState('');
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [showPaceInfo, setShowPaceInfo] = React.useState(false);
  const paceDefs = ctx.state.paceGroupDefs || [];
  const canSubmit = !busy && (!s.paceGroups || paceGroup);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try { await ctx.bookSession(s.id, paceGroup, note.trim()); ctx.closeModal(); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="book-with-note-modal">
      {s.paceGroups && (
        <Field label="Pace group">
          <div className="pace-select-wrap">
            <select value={paceGroup} onChange={(e) => setPaceGroup(e.target.value)} data-testid="book-with-note-pace-select">
              <option value="">Pick your pace group…</option>
              {paceDefs.map(g => <option key={g.key} value={g.key}>{g.emoji} {g.label} /mile · {g.kmLabel} /km</option>)}
            </select>
            <button
              type="button"
              className="pace-info-btn"
              data-testid="book-with-note-pace-info-btn"
              title="What do pace groups mean?"
              aria-label="What do pace groups mean?"
              onClick={() => setShowPaceInfo(v => !v)}
            >
              <Info size={14} />
            </button>
          </div>
          {showPaceInfo && (
            <div className="pace-info" data-testid="book-with-note-pace-info" style={{ marginTop: 8, fontSize: 13 }}>
              <p style={{ marginBottom: 8 }}><strong>🔵 Blue</strong> — our steadiest, social pace. <strong>🟡 Yellow</strong> — a quicker, tempo-ish pace.</p>
              <p style={{ marginBottom: 0 }}>Pick whichever is closest to your natural running speed — shown per mile and per km above.</p>
            </div>
          )}
        </Field>
      )}
      <Field label="Dietary requirements / notes (optional)">
        <textarea rows={3} maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. vegetarian, nut allergy, gluten-free…" data-testid="book-with-note-input" />
      </Field>
      <button
        type="button"
        className="btn"
        style={{ width: '100%', marginTop: 6 }}
        disabled={!canSubmit}
        onClick={submit}
        data-testid="book-with-note-submit"
      >
        {busy ? 'Booking…' : (s.price && !/free/i.test(s.price) ? `Book · ${s.price}` : 'Book free')}
      </button>
    </div>
  );
}

function AddMemberToSession({ state, ctx, s }) {
  const [q, setQ] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const bookedIds = new Set((s.bookedMembers || []).map(m => m.id));
  const results = q.trim().length < 2 ? [] : state.members
    .filter(m => m.role !== 'Admin' && !bookedIds.has(m.id) && (m.name || '').toLowerCase().includes(q.trim().toLowerCase()))
    .slice(0, 6);

  const add = async (m) => {
    setBusy(true);
    const ok = await ctx.adminAddMemberToSession(s.id, m.id);
    setBusy(false);
    if (ok) setQ('');
  };

  return (
    <div className="notice soft" data-testid="add-member-to-session" style={{ marginBottom: 4 }}>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>Add a member (no payment — already paid elsewhere)</div>
      <input
        value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search member by name…"
        disabled={busy} data-testid="add-member-search-input"
      />
      {results.length > 0 && (
        <div className="list" style={{ marginTop: 8 }}>
          {results.map(m => (
            <div className="item" key={m.id} style={{ cursor: 'pointer', padding: '8px 10px' }} onClick={() => !busy && add(m)} data-testid={`add-member-result-${m.id}`}>
              <span>{m.name}</span>
              <button type="button" className="btn secondary" style={{ padding: '4px 10px' }} disabled={busy}>Add</button>
            </div>
          ))}
        </div>
      )}
      {q.trim().length >= 2 && results.length === 0 && <div className="meta" style={{ marginTop: 6 }}>No matching members found (or already booked).</div>}
    </div>
  );
}

function AddGuestToSession({ ctx, s }) {
  const [name, setName] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const add = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    const ok = await ctx.adminAddGuestToSession(s.id, trimmed);
    setBusy(false);
    if (ok) setName('');
  };

  return (
    <div className="notice soft" data-testid="add-guest-to-session" style={{ marginBottom: 4 }}>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>Add a guest (not an SPC member)</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={name} onChange={(e) => setName(e.target.value)} placeholder="Guest's name…" maxLength={60}
          disabled={busy} data-testid="add-guest-name-input" style={{ flex: 1 }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <button type="button" className="btn secondary" disabled={busy || !name.trim()} data-testid="add-guest-submit-btn" onClick={add}>Add guest</button>
      </div>
    </div>
  );
}

function EditSession({ state, ctx, payload }) {
  const s = state.sessions.find(x => x.id === payload.id);
  const [tab, setTab] = React.useState('edit'); // 'edit' | 'danger'
  const [applyToSeries, setApplyToSeries] = React.useState(false);
  const [photo, setPhoto] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [priceInput, setPriceInput] = React.useState(s?.priceAmount ?? '');
  const [blockBooking, setBlockBooking] = React.useState(!!s?.blockBooking);
  const [paceGroups, setPaceGroups] = React.useState(!!s?.paceGroups);
  const [hasCatering, setHasCatering] = React.useState(!!s?.hasCatering);

  if (!s) return <div className="empty">Session not found — it may already have been cancelled. Refresh the page.</div>;
  const isSeries = s.recurring && s.seriesId;

  const submit = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const dateISO = f.get('dateISO');
    const ptsRaw = f.get('points');
    const body = {
      title: f.get('title'),
      type: f.get('type'),
      dateISO,
      date: dateISO ? (formatDisplay(dateISO) || dateISO) : undefined,
      time: f.get('time'),
      location: f.get('location'),
      coach: f.get('coach'),
      capacity: Number(f.get('capacity')),
      priceAmount: (() => { const v = f.get('priceAmount'); return v === null || String(v).trim() === '' ? 0 : Number(v); })(),
      duration: f.get('duration'),
      level: f.get('level'),
      description: f.get('description'),
      points: ptsRaw === null || String(ptsRaw).trim() === '' ? null : Number(ptsRaw),
      memberPaysFee: !!f.get('memberPaysFee'),
      featured: !!f.get('featured'),
      notifyMembers: s.booked > 0 ? !!f.get('notifyMembers') : false,
      photo,
      applyToSeries,
    };
    if (isSeries && s.type === 'Classes') body.blockBooking = blockBooking;
    if (s.type === 'Run Session') body.paceGroups = paceGroups;
    body.hasCatering = hasCatering;
    setBusy(true);
    const ok = await ctx.editSession(s.id, body);
    setBusy(false);
    if (ok) ctx.closeModal();
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name} from this session? They won't be notified.`)) return;
    await ctx.adminRemoveMemberFromSession(s.id, m.id);
  };

  const transferMember = async (m, target, mode) => {
    return await ctx.adminTransferMember(s.id, m.id, target.id, mode);
  };

  const removeGuest = async (g) => {
    if (!window.confirm(`Remove guest ${g.name} from this session?`)) return;
    await ctx.adminRemoveGuestFromSession(s.id, g.id);
  };

  return (
    <div>
      <div className="arcade-tabs" style={{ marginBottom: 12 }}>
        <button className={`arcade-tab ${tab === 'edit' ? 'active' : ''}`} onClick={() => setTab('edit')} data-testid="tab-edit">Edit</button>
        <button className={`arcade-tab ${tab === 'danger' ? 'active' : ''}`} onClick={() => setTab('danger')} data-testid="tab-danger">Cancel</button>
      </div>

      {tab === 'edit' ? (
        <form className="form" onSubmit={submit} data-testid="edit-session-form">
          <div className="notice soft">Editing <strong>{s.title}</strong> — {s.date} · {s.time}. {s.booked} booked.</div>
          <SessionSafetyCards ctx={ctx} s={s} />
          <AddMemberToSession state={state} ctx={ctx} s={s} />
          {s.type === 'Social' && <AddGuestToSession ctx={ctx} s={s} />}
          <BookedMembersList s={s} onRemove={removeMember} onTransfer={transferMember} onRemoveGuest={removeGuest} />
          {isSeries && (
            <label className="repeat-row" data-testid="apply-series-row">
              <input type="checkbox" checked={applyToSeries} onChange={(e) => setApplyToSeries(e.target.checked)} data-testid="apply-to-series" />
              <span><strong>Apply changes to the whole weekly series</strong><small>Date &amp; time on this record still change only for this occurrence.</small></span>
            </label>
          )}
          {isSeries && s.type === 'Classes' && (
            <label className="repeat-row" data-testid="edit-block-booking-toggle">
              <input type="checkbox" checked={blockBooking} onChange={(e) => setBlockBooking(e.target.checked)} />
              <span><strong>This is a block booking</strong><small>Members book & pay for the whole block in one go — no single-week booking option.</small></span>
            </label>
          )}
          {s.type === 'Run Session' && (
            <label className="repeat-row" data-testid="edit-pace-groups-toggle">
              <input type="checkbox" checked={paceGroups} onChange={(e) => setPaceGroups(e.target.checked)} />
              <span><strong>Use pace groups</strong><small>Members pick a pace group (🔵 10:30-13:00 / 🟡 9:30-10:30) when booking.</small></span>
            </label>
          )}
          <label className="repeat-row" data-testid="edit-has-catering-toggle">
            <input type="checkbox" checked={hasCatering} onChange={(e) => setHasCatering(e.target.checked)} />
            <span><strong>This event includes food</strong><small>Members get asked for dietary requirements / allergies when booking.</small></span>
          </label>

          <Field label="Type"><input name="type" defaultValue={s.type} required data-testid="edit-type" /></Field>
          <Field label="Title"><input name="title" defaultValue={s.title} required data-testid="edit-title" /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Date"><input name="dateISO" type="date" defaultValue={s.dateISO} required data-testid="edit-date" /></Field>
            <Field label="Time"><input name="time" type="time" defaultValue={s.time ? s.time.split(':').map((p, i) => i === 0 ? String(p).padStart(2, '0') : p).join(':') : ''} required data-testid="edit-time" /></Field>
          </div>
          <Field label="Location"><input name="location" defaultValue={s.location} required data-testid="edit-location" /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Coach"><input name="coach" defaultValue={s.coach} data-testid="edit-coach" /></Field>
            <Field label="Capacity"><input name="capacity" type="number" min="1" defaultValue={s.capacity} required data-testid="edit-capacity" /></Field>
          </div>
          <Field label="Duration"><input name="duration" defaultValue={s.duration || ''} data-testid="edit-duration" /></Field>
          <Field label={isSeries && s.type === 'Classes' && blockBooking ? 'Price for the WHOLE block (£)' : 'Price (£, optional)'}>
            <input name="priceAmount" type="number" min="0" step="0.01" value={priceInput} onChange={(e) => setPriceInput(e.target.value)} placeholder={isSeries && s.type === 'Classes' && blockBooking ? 'Total for all weeks, e.g. 90' : '0 or blank = free'} data-testid="edit-price-amount" />
          </Field>
          {isSeries && s.type === 'Classes' && blockBooking && <div className="notice">This is the TOTAL price for the whole block — members pay it once, not per week.</div>}
          {s.booked > 0 && s.priceAmount > 0 && <div className="notice">{s.booked} member(s) already paid for this session — changing the amount only affects new bookings.</div>}
          {Number(priceInput) > 0 && (
            <label className="repeat-row" data-testid="edit-session-fee-toggle">
              <input type="checkbox" name="memberPaysFee" defaultChecked={!!s.memberPaysFee} />
              <span>Member pays the card fee (~2% + 20p ≈ £{(Number(priceInput) * 0.02 + 0.20).toFixed(2)} on top) — otherwise the club absorbs it</span>
            </label>
          )}
          <Field label="Level"><input name="level" defaultValue={s.level || 'All levels'} data-testid="edit-level" /></Field>
          <Field label="SPC Points on attendance"><input name="points" type="number" min="0" defaultValue={s.points ?? ''} placeholder="Leave blank to use the club default" data-testid="edit-points" /></Field>
          <Field label="Description"><textarea name="description" defaultValue={s.description || ''} data-testid="edit-description" /></Field>
          {s.hasPhoto && !photo && (
            <div className="sess-photo-preview" style={{ marginBottom: 4 }} data-testid="edit-session-current-photo">
              <img src={ctx.sessionPhotoUrl(s.id, s.photoV)} alt={s.title} />
              <div className="meta" style={{ marginTop: 4 }}>Current photo on file — upload a new one below to replace it.</div>
            </div>
          )}
          <CoverPhotoPicker photo={photo} setPhoto={setPhoto} toast={ctx.toast} resizeMax={1600} label="Cover photo" testidPrefix="edit-session-photo" />
          <label className="repeat-row" data-testid="edit-session-featured-toggle">
            <input type="checkbox" name="featured" defaultChecked={!!s.featured} />
            <span>Feature on Homepage — shows a flashy "Book now" banner at the top of Home until this session's date</span>
          </label>
          {s.booked > 0 && (
            <label className="repeat-row" data-testid="edit-session-notify-toggle">
              <input type="checkbox" name="notifyMembers" defaultChecked={true} />
              <span><strong>Notify booked members about this change</strong><small>Uncheck for minor tweaks that don't need a push/notification — {s.booked} member{s.booked === 1 ? '' : 's'} currently booked.</small></span>
            </label>
          )}
          <button className="btn" disabled={busy} data-testid="edit-save-btn">{busy ? 'Saving…' : (applyToSeries ? 'Save & apply to series' : 'Save changes')}</button>
        </form>
      ) : (
        <div className="form">
          {s.hasPhoto && <div className="sess-photo-preview" style={{ marginBottom: 4 }}><img src={ctx.sessionPhotoUrl(s.id, s.photoV)} alt={s.title} /></div>}
          <div className="notice"><strong>{s.title}</strong><br />{s.date} · {s.time} · {s.location}<br />{s.booked}/{s.capacity} booked · {(!s.price || /free/i.test(s.price)) ? 'Free' : s.price}</div>
          {isSeries && <div className="notice soft" data-testid="series-note"><strong>Part of a weekly series.</strong> You can cancel just this date, or end the whole series.</div>}
          <div className="notice soft">{s.booked > 0 ? `${s.booked} booked ${s.booked === 1 ? 'member' : 'members'} will be notified.` : 'No members are booked on this session.'}</div>
          <BookedMembersList s={s} />
          <button className="btn danger" data-testid="cancel-session-btn" onClick={async () => {
            const attended = s.attended || 0;
            const msg = attended > 0
              ? `${attended} member(s) already checked in to this session — deleting will PERMANENTLY lose their attendance record. Delete anyway?`
              : `Cancel this ONE session on ${s.date}? Booked members will be notified. ${isSeries ? 'Other dates in the weekly series are NOT affected.' : ''}`;
            if (!window.confirm(msg)) return;
            const ok = await ctx.deleteSession(s.id, attended > 0);
            if (ok) ctx.closeModal();
          }}>{isSeries ? 'Cancel this date only' : 'Cancel this session'}</button>
          {isSeries && <button className="btn ghost" data-testid="cancel-series-btn" style={{ marginTop: 10 }} onClick={() => { if (window.confirm('End the ENTIRE weekly series? All upcoming dates in this series will be removed and booked members notified.')) { ctx.cancelSeries(s.seriesId); ctx.closeModal(); } }}>End entire weekly series</button>}
        </div>
      )}
    </div>
  );
}

